import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, finalize, forkJoin, of } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleSubmissionsService } from 'src/app/services/api/schedule-submissions/schedule-submissions.service';
import {
  ProjectResearchSubjectBlockModel,
  ProjectResearchTeachingTimeModel,
  ScheduleSubmissionCorrectionRequestModel,
  ScheduleSubmissionBlockModel,
  ScheduleSubmissionProjectResearchDetailResponse,
  ScheduleSubmissionStatusHistoryRow,
  ScheduleSubmissionSelectedTeachingDateModel,
  ScheduleSubmissionSubjectModel,
  ScheduleSubmissionTeachingTypeModel,
  ScheduleSubmissionSubjectScheduleDetailResponse,
  ScheduleSubmissionTeacherSelectionModel,
  ScheduleSubmissionVersionDetail,
  ScheduleSubmissionVersionSummary,
  ScheduleSubmissionsTableModel,
  SubjectScheduleBlockModel,
} from 'src/app/services/models/schedule/schedule-submissions.model';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { RoleID } from 'src/utils/enum/role.enum';
import {
  getScheduleSubmissionListStatusDisplay,
  normalizeScheduleSubmissionListStatusCode,
  ScheduleSubmissionListDisplayState,
} from '../schedule-submission-status-display';
import { scheduleVersionSnapshotToSubmission } from './schedule-version-snapshot.mapper';
import { getApiErrorMessage } from 'src/utils/http-error-message';

const EDUCATION_HEAD_DEADLINE_BLOCKED_REASON = 'เลยวันที่ส่งเบิกแล้ว\nไม่สามารถรับรองได้';

interface TimetableHour {
  label: string;
  colSpan: number;
}

interface TimetableDay {
  dayOfWeek: number;
  thaiName: string;
  englishName: string;
}

interface TimetableEvent {
  approvalId: number | null;
  startSlot: number;
  endSlot: number;
  subjectLabel: string;
  detailLabel: string;
  statusCode: CourseDisplayStatusCode;
  statusLabel: string;
  courseDetailTarget: CourseDetailTarget;
}

interface TimetableCellEvent extends TimetableEvent {
  offsetSlots: number;
  spanSlots: number;
}

interface TimetableCell {
  startSlot: number;
  colSpan: number;
  events: TimetableCellEvent[];
}

interface TimetableRow extends TimetableDay {
  cells: TimetableCell[];
}

interface ProjectResearchRow {
  id: number;
  approvalIds: number[];
  projectResearchSubjectId: number;
  subjectCode: string;
  subjectName: string;
  teachingDays: string;
  teachingTimes: string;
  statusCode: CourseDisplayStatusCode;
  statusLabel: string;
}

interface ApprovalStatusSummary {
  status: CourseDisplayStatusCode;
  label: string;
  count: number;
}

type CourseDetailTarget =
  | {
    type: 'subject_schedule';
    subjectScheduleId: number;
  }
  | {
    type: 'project_research_subject';
    projectResearchSubjectId: number;
  };

interface CourseDetail {
  type: CourseDetailTarget['type'];
  subjectId: number | null;
  approvalId: number | null;
  approvalIds: number[];
  actionApprovalStatusCode: ApprovalStatusCode;
  approvalStatusCode: CourseDisplayStatusCode;
  approvalStatusLabel: string;
  courseTypeLabel: string;
  subjectCode: string;
  subjectName: string;
  curriculumType: string;
  creditDescription: string;
  section: string;
  studentCount: string;
  teachingDays: string;
  teachingTimes: string;
  teachingHours: string;
  teachingType: string;
  workloadSplitLabel: string;
  roomName: string;
  primaryAdvisorStudentCount: string;
  coAdvisorStudentCount: string;
  description: string;
  teachers: CourseTeacherDetail[];
}

type ApprovalStatusCode =
  | 'draft'
  | 'imported_pending_fix'
  | 'ready_for_teacher'
  | 'teacher_confirmed'
  | 'education_head_confirmed'
  | 'vice_dean_confirmed'
  | 'locked'
  | 'special_revision_requested'
  | 'revision_requested'
  | 'unknown';

type CourseDisplayStatusCode =
  | ApprovalStatusCode
  | 'waiting_co_teacher'
  | 'waiting_teacher_submit'
  | 'waiting_education_head_review';

type CorrectionRequestHistoryStatusCode =
  | 'correction_requested'
  | 'correction_approved'
  | 'correction_rejected'
  | 'correction_opened'
  | 'correction_completed';

type StatusHistoryDisplayCode = CourseDisplayStatusCode | CorrectionRequestHistoryStatusCode;

interface CourseTeacherDetail {
  fullName: string;
  department: string;
  teachingRanges: string[];
  teachingHours: string;
  statusCode: ApprovalStatusCode;
  statusLabel: string;
}

type CorrectionApprovableType = 'ScheduleSubmissionSubjectSchedule' | 'ProjectResearchSubjectAssignment';

interface CorrectionCourseRow {
  key: string;
  approvableType: CorrectionApprovableType;
  approvableId: number;
  subjectCode: string;
  subjectName: string;
  section: string;
  teachingType: string;
  teachingDays: string;
  teachingTimes: string;
  roomName: string;
  workloadSplitLabel: string;
  statusCode: CourseDisplayStatusCode;
  statusLabel: string;
  selected: boolean;
  teacherNote: string;
  touched: boolean;
}

interface StatusHistoryRow {
  id: string | number;
  sequence: number;
  historyType: string;
  historyTypeLabel: string;
  subjectCode: string;
  subjectName: string;
  section: string;
  teachingType: string;
  resultCode: string;
  resultLabel: string;
  actorName: string;
  actedAt: string;
  comment: string;
}

interface StatusHistoryDataSource {
  data: StatusHistoryRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

type ScheduleSubmissionDetailTab = 'schedule' | 'status_history';
type CourseReviewAction = 'approve' | 'reject';
type ConfirmationAction = 'approve' | 'reject' | 'submit_teacher_review';
type SubjectScheduleDetailBlock = ScheduleSubmissionSubjectScheduleDetailResponse['block'];
type VersionQuerySelection =
  | { kind: 'id' | 'version_no' | 'legacy_id'; value: number }
  | { kind: 'invalid' }
  | null;

const TIMETABLE_START_HOUR = 7;
const TIMETABLE_END_HOUR = 21;
const TIMETABLE_SLOT_MINUTES = 30;
const TIMETABLE_SLOTS_PER_HOUR = 60 / TIMETABLE_SLOT_MINUTES;
const TIMETABLE_TOTAL_SLOTS = ((TIMETABLE_END_HOUR - TIMETABLE_START_HOUR) * 60) / TIMETABLE_SLOT_MINUTES;
const SCHEDULE_VERSION_VIEWER_ENABLED = false;
// ADR-EO: the API keeps a schedule whose correction removed every course in the review queues
// only while that correction is open, so the bottom action follows the same window.
const SPECIAL_CORRECTION_REVIEW_CYCLE_STATUSES = ['editing', 'resubmitted_for_approval'];

const TIMETABLE_HOURS: TimetableHour[] = Array.from(
  { length: TIMETABLE_END_HOUR - TIMETABLE_START_HOUR },
  (_, index) => {
    const hour = TIMETABLE_START_HOUR + index;

    return {
      label: `${hour}:00-${hour + 1}:00`,
      colSpan: 60 / TIMETABLE_SLOT_MINUTES,
    };
  }
);

const TIMETABLE_DAYS: TimetableDay[] = [
  { dayOfWeek: 1, thaiName: 'วันจันทร์', englishName: 'Monday' },
  { dayOfWeek: 2, thaiName: 'วันอังคาร', englishName: 'Tuesday' },
  { dayOfWeek: 3, thaiName: 'วันพุธ', englishName: 'Wednesday' },
  { dayOfWeek: 4, thaiName: 'วันพฤหัสบดี', englishName: 'Thursday' },
  { dayOfWeek: 5, thaiName: 'วันศุกร์', englishName: 'Friday' },
  { dayOfWeek: 6, thaiName: 'วันเสาร์', englishName: 'Saturday' },
  { dayOfWeek: 7, thaiName: 'วันอาทิตย์', englishName: 'Sunday' },
];

@Component({
  selector: 'app-schedule-submissions-details',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    TranslateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './schedule-submissions-details.component.html',
  styleUrl: './schedule-submissions-details.component.scss',
})
export class ScheduleSubmissionsDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('pdfExportErrorDialog') pdfExportErrorDialog?: ConfirmDialogComponent;
  @ViewChild('courseApproveConfirmDialog') courseApproveConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('workflowConfirmDialog') workflowConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('workflowSuccessDialog') workflowSuccessDialog?: ConfirmDialogComponent;
  @ViewChild('workflowErrorDialog') workflowErrorDialog?: ConfirmDialogComponent;
  @ViewChild('correctionSubmitConfirmDialog') correctionSubmitConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('statusHistoryStatusTemplate') set statusHistoryStatusTemplate(template: TemplateRef<any> | undefined) {
    if (template && template !== this._statusHistoryStatusTemplate) {
      this._statusHistoryStatusTemplate = template;
      this.initStatusHistoryColumns();
    }
  }
  private _statusHistoryStatusTemplate: TemplateRef<any> | undefined;

  readonly isScheduleVersionViewerEnabled = SCHEDULE_VERSION_VIEWER_ENABLED;
  readonly timetableHours = TIMETABLE_HOURS;
  readonly timetableRows = signal<TimetableRow[]>(this.buildTimetableRows([]));
  readonly projectResearchRows = signal<ProjectResearchRow[]>([]);
  readonly selectedProjectResearchRow = signal<ProjectResearchRow | null>(null);
  readonly selectedCourseDetail = signal<CourseDetail | null>(null);
  readonly isCourseDetailModalOpen = signal(false);
  readonly isCourseDetailLoading = signal(false);
  readonly isSubmissionActionLoading = signal(false);
  readonly isPdfExportLoading = signal(false);
  readonly pdfExportErrorMessage = signal('');
  readonly courseDetailErrorMessage = signal('');
  readonly approvalActionErrorMessage = signal('');
  readonly submissionActionErrorMessage = signal('');
  readonly rejectCommentValue = signal('');
  readonly isRejectCommentModalOpen = signal(false);
  readonly isCourseApproveConfirmModalOpen = signal(false);
  readonly isReviewConfirmModalOpen = signal(false);
  readonly isReviewSuccessModalOpen = signal(false);
  readonly isCorrectionModalOpen = signal(false);
  readonly isCorrectionSubmitConfirmModalOpen = signal(false);
  readonly isCorrectionSubmitting = signal(false);
  readonly correctionRequestSubmitted = signal(false);
  readonly correctionRows = signal<CorrectionCourseRow[]>([]);
  readonly correctionSubmitAttempted = signal(false);
  readonly correctionErrorMessage = signal('');
  readonly showNewCourseRequestNote = signal(false);
  readonly newCourseRequestNoteControl = new FormControl('', { nonNullable: true });
  readonly activeDetailTab = signal<ScheduleSubmissionDetailTab>('schedule');
  readonly isStatusHistoryLoading = signal(false);
  readonly isStatusHistoryLoaded = signal(false);
  readonly statusHistoryErrorMessage = signal('');
  readonly statusHistorySearchTerm = signal('');
  readonly statusHistoryRows = signal<StatusHistoryRow[]>([]);
  readonly statusHistoryDataSource = signal<StatusHistoryDataSource>(this.createStatusHistoryDataSource([]));
  readonly statusHistoryPageSizeOptions = [10, 20, 50];
  statusHistoryColumns: any[] = [];
  private statusHistoryCurrentPage = 1;
  private statusHistoryCurrentLimit = 50;
  private statusHistoryRequestId = 0;
  private statusHistoryRequest?: Subscription;
  readonly reviewSuccessMessage = signal('');
  readonly workflowSuccessTitle = signal('ดำเนินการสำเร็จ');
  readonly workflowErrorTitle = signal('ไม่สามารถดำเนินการได้');
  readonly pendingConfirmationAction = signal<ConfirmationAction | null>(null);
  readonly isEducationHeadConfirming = signal(false);
  readonly isLoading = signal(false);
  readonly isVersionListLoading = signal(false);
  readonly isVersionSnapshotLoading = signal(false);
  readonly versionErrorMessage = signal('');
  readonly scheduleVersions = signal<ScheduleSubmissionVersionSummary[]>([]);
  readonly selectedVersionId = signal<number | null>(null);
  readonly selectedVersionDetail = signal<ScheduleSubmissionVersionDetail | null>(null);
  readonly isVersionDropdownOpen = signal(false);
  readonly teacherName = signal('-');
  readonly departmentName = signal('-');
  readonly scheduleSubmission = signal<ScheduleSubmissionsTableModel | null>(null);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly scheduleSubmissionsService = inject(ScheduleSubmissionsService);
  private readonly authService = inject(AuthService);

  private scheduleSubmissionId?: number;
  private courseDetailRequestId = 0;
  private liveScheduleSubmission: ScheduleSubmissionsTableModel | null = null;
  private pendingRejectDetail: CourseDetail | null = null;
  private pendingApproveDetail: CourseDetail | null = null;
  private readonly correctionNoteByKey = new Map<string, string>();
  private readonly correctionTouchedKeys = new Set<string>();

  get submissionActionError(): string {
    return this.submissionActionErrorMessage();
  }
  ngOnInit(): void {
    this.initStatusHistoryColumns();

    const submissionId = Number(this.route.snapshot.paramMap.get('id'));

    if (!Number.isFinite(submissionId) || submissionId <= 0) {
      this.navigateBack();
      return;
    }

    this.loadScheduleSubmission(submissionId);
  }

  ngOnDestroy(): void {
    this.statusHistoryRequestId += 1;
    this.statusHistoryRequest?.unsubscribe();
  }

  onBack(): void {
    this.navigateBack();
  }

  canExportPdf(): boolean {
    return !this.isApprovalReviewMode() &&
      !this.isViewingHistoricalVersion() &&
      this.scheduleSubmission()?.can_export_pdf === true;
  }

  onExportPdf(): void {
    const submissionId = this.scheduleSubmissionId;
    if (submissionId == null || !this.canExportPdf() || this.isPdfExportLoading()) {
      return;
    }

    this.isPdfExportLoading.set(true);
    this.pdfExportErrorMessage.set('');
    this.scheduleSubmissionsService.exportScheduleSubmissionPdf(submissionId).subscribe({
      next: (response) => {
        try {
          this.downloadPdfResponse(response, submissionId);
          this.isPdfExportLoading.set(false);
        } catch (error) {
          this.showPdfExportError(this.extractErrorMessage(error));
        }
      },
      error: (error) => {
        void (async (): Promise<void> => {
          let message = this.extractErrorMessage(error);
          if (error?.error instanceof Blob) {
            try {
              const payload = JSON.parse(await error.error.text());
              message = this.extractErrorMessage({ error: payload });
            } catch {
              // Keep the standard API error when the Blob is not JSON.
            }
          }
          this.showPdfExportError(message);
        })();
      },
    });
  }

  private showPdfExportError(message: string): void {
    this.pdfExportErrorMessage.set(message || 'ไม่สามารถดาวน์โหลดไฟล์ PDF ได้');
    this.isPdfExportLoading.set(false);
    this.pdfExportErrorDialog?.open();
  }

  private downloadPdfResponse(response: HttpResponse<Blob>, submissionId: number): void {
    if (response.body == null) {
      throw new Error('ไม่พบข้อมูลไฟล์ PDF');
    }

    const filename = this.pdfFilename(response, submissionId);
    const url = window.URL.createObjectURL(response.body);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private pdfFilename(response: HttpResponse<Blob>, submissionId: number): string {
    const disposition = response.headers.get('content-disposition') ?? '';
    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    const rawFilename = encodedMatch?.[1] ?? plainMatch?.[1];

    if (rawFilename == null) {
      return `approved-teaching-schedule-${submissionId}.pdf`;
    }

    try {
      return decodeURIComponent(rawFilename);
    } catch {
      return rawFilename;
    }
  }

  onToggleVersionDropdown(): void {
    if (!this.isScheduleVersionViewerEnabled) {
      return;
    }

    if (this.scheduleVersions().length === 0) {
      return;
    }

    this.isVersionDropdownOpen.set(!this.isVersionDropdownOpen());
  }

  onCloseVersionDropdown(): void {
    this.isVersionDropdownOpen.set(false);
  }

  onSelectVersion(versionId: number): void {
    if (!this.isScheduleVersionViewerEnabled) {
      return;
    }

    if (!Number.isFinite(versionId) || versionId <= 0 || versionId === this.selectedVersionId()) {
      this.onCloseVersionDropdown();
      return;
    }

    this.onCloseVersionDropdown();
    this.selectVersion(versionId, true);
  }

  onSelectDetailTab(tab: ScheduleSubmissionDetailTab): void {
    this.activeDetailTab.set(tab);

    if (tab === 'status_history' && !this.isStatusHistoryLoaded()) {
      this.statusHistoryCurrentPage = 1;
      this.loadStatusHistory();
    }
  }

  onStatusHistorySearchChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.statusHistorySearchTerm.set(target?.value ?? '');
    this.statusHistoryCurrentPage = 1;
    this.loadStatusHistory();
  }

  onClearStatusHistorySearch(): void {
    this.statusHistorySearchTerm.set('');
    this.statusHistoryCurrentPage = 1;
    this.loadStatusHistory();
  }

  onStatusHistoryPageChange(page: number): void {
    this.statusHistoryCurrentPage = page;
    this.loadStatusHistory();
  }

  onStatusHistoryLimitChange(limit: number): void {
    this.statusHistoryCurrentLimit = limit;
    this.statusHistoryCurrentPage = 1;
    this.loadStatusHistory();
  }

  private initStatusHistoryColumns(): void {
    this.statusHistoryColumns = [
      {
        prop: 'sequence',
        name: 'ลำดับ',
        sortable: false,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        cellClass: 'text-center',
        headerClass: 'd-flex justify-content-center',
      },
      {
        prop: 'historyTypeLabel',
        name: 'ประเภทประวัติ',
        sortable: false,
        width: 160,
        minWidth: 140,
      },
      {
        prop: 'subjectCode',
        name: 'รหัสวิชา',
        sortable: false,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'subjectName',
        name: 'ชื่อวิชา',
        sortable: false,
        width: 240,
        minWidth: 220,
      },
      {
        prop: 'section',
        name: 'Section',
        sortable: false,
        width: 100,
        minWidth: 96,
      },
      {
        prop: 'resultLabel',
        name: 'ผลการดำเนินการ',
        sortable: false,
        cellTemplate: this._statusHistoryStatusTemplate,
        width: 170,
        minWidth: 160,
      },
      {
        prop: 'actorName',
        name: 'ชื่อ - นามสกุล',
        sortable: false,
        width: 180,
        minWidth: 160,
      },
      {
        prop: 'actedAt',
        name: 'วันที่อัปเดตสถานะ',
        sortable: false,
        width: 190,
        minWidth: 180,
        cellClass: 'text-nowrap',
      },
      {
        prop: 'comment',
        name: 'เหตุผล',
        sortable: false,
        width: 240,
        minWidth: 200,
      },
    ];
  }

  private createStatusHistoryDataSource(
    rows: StatusHistoryRow[],
    page = 1,
    limit = this.statusHistoryCurrentLimit,
    total = rows.length,
    totalPage = Math.max(1, Math.ceil(rows.length / limit)),
    offset = 0,
  ): StatusHistoryDataSource {
    return {
      data: rows,
      limit,
      offset,
      page,
      total,
      total_page: totalPage,
    };
  }

  private loadStatusHistory(): void {
    const submissionId = this.scheduleSubmissionId;
    if (submissionId == null) {
      return;
    }

    const requestId = ++this.statusHistoryRequestId;
    this.statusHistoryRequest?.unsubscribe();
    this.isStatusHistoryLoading.set(true);
    this.statusHistoryErrorMessage.set('');
    this.statusHistoryRows.set([]);
    this.statusHistoryDataSource.set(this.createStatusHistoryDataSource(
      [],
      this.statusHistoryCurrentPage,
      this.statusHistoryCurrentLimit,
      0,
      1,
      (this.statusHistoryCurrentPage - 1) * this.statusHistoryCurrentLimit,
    ));

    this.statusHistoryRequest = this.scheduleSubmissionsService.getStatusHistory(submissionId, {
      page: this.statusHistoryCurrentPage,
      limit: this.statusHistoryCurrentLimit,
      keywords: this.statusHistorySearchTerm().trim(),
    }).pipe(
      finalize(() => {
        if (requestId === this.statusHistoryRequestId) {
          this.isStatusHistoryLoading.set(false);
        }
      }),
    ).subscribe({
      next: (response) => {
        if (requestId !== this.statusHistoryRequestId) {
          return;
        }

        const rows = response.data.map((row) => this.toStatusHistoryRow(row));
        const page = Number(response.page ?? response.meta?.page ?? this.statusHistoryCurrentPage);
        const limit = Number(response.limit ?? response.meta?.limit ?? this.statusHistoryCurrentLimit);
        const total = Number(response.total ?? response.meta?.total ?? rows.length);
        const totalPage = Number(response.total_page ?? response.total_pages ?? response.meta?.total_pages ?? 1);
        const offset = Number(response.offset ?? ((page - 1) * limit));

        this.statusHistoryCurrentPage = page;
        this.statusHistoryCurrentLimit = limit;
        this.statusHistoryRows.set(rows);
        this.statusHistoryDataSource.set(this.createStatusHistoryDataSource(
          rows,
          page,
          limit,
          total,
          totalPage,
          offset,
        ));
        this.isStatusHistoryLoaded.set(true);
      },
      error: (err) => {
        if (requestId !== this.statusHistoryRequestId) {
          return;
        }

        this.statusHistoryErrorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private toStatusHistoryRow(row: ScheduleSubmissionStatusHistoryRow): StatusHistoryRow {
    const statusCode = this.normalizeFlatStatusHistoryCode(row.status);

    return {
      id: row.id,
      sequence: Number(row.sequence ?? 0),
      historyType: row.history_type || 'teaching_schedule',
      historyTypeLabel: row.history_type_label || 'ตารางสอน',
      subjectCode: this.formatDisplayValue(row.subject_code),
      subjectName: this.formatDisplayValue(row.subject_name),
      section: this.formatDisplayValue(row.section),
      teachingType: this.formatDisplayValue(row.teaching_type),
      resultCode: row.result_code || statusCode,
      resultLabel: row.result_label || this.getStatusHistoryLabel(statusCode),
      actorName: (row.actor_display_name || row.actor_name || '').trim() || '-',
      actedAt: this.formatHistoryDateTime(row.acted_at),
      comment: row.comment?.trim() || '-',
    };
  }

  onViewProjectResearch(row: ProjectResearchRow): void {
    this.selectedProjectResearchRow.set(row);
    this.openCourseDetail({
      type: 'project_research_subject',
      projectResearchSubjectId: row.projectResearchSubjectId,
    });
  }

  onTimetableCellClick(cell: TimetableCell): void {
    if (cell.events.length !== 1) {
      return;
    }

    this.openCourseDetail(cell.events[0].courseDetailTarget);
  }

  onTimetableEventClick(event: TimetableEvent, domEvent: Event): void {
    domEvent.stopPropagation();
    this.openCourseDetail(event.courseDetailTarget);
  }

  onCloseCourseDetail(): void {
    this.courseDetailRequestId += 1;
    this.isCourseDetailModalOpen.set(false);
    this.isCourseDetailLoading.set(false);
    this.courseDetailErrorMessage.set('');
    this.approvalActionErrorMessage.set('');
    this.selectedCourseDetail.set(null);
    this.selectedProjectResearchRow.set(null);
    this.pendingApproveDetail = null;
    this.isCourseApproveConfirmModalOpen.set(false);
  }

  onApproveSelectedCourse(): void {
    const detail = this.selectedCourseDetail();

    if (detail == null || detail.approvalIds.length === 0 || !this.canApproveSelectedCourse()) {
      return;
    }

    this.pendingApproveDetail = detail;
    this.isCourseApproveConfirmModalOpen.set(true);
    this.courseApproveConfirmDialog?.open();
  }

  onCancelCourseApproveConfirm(): void {
    this.pendingApproveDetail = null;
    this.isCourseApproveConfirmModalOpen.set(false);
  }

  onConfirmCourseApprove(): void {
    const detail = this.pendingApproveDetail;

    if (detail == null || detail.approvalIds.length === 0 || this.isSubmissionActionLoading()) {
      return;
    }

    this.submitCourseReviewDecision(detail, 'approve');
  }

  onRejectSelectedCourse(): void {
    const detail = this.selectedCourseDetail();

    if (detail == null || detail.approvalIds.length === 0 || !this.canRejectSelectedCourse()) {
      return;
    }

    this.pendingRejectDetail = detail;
    this.rejectCommentValue.set('');
    this.isRejectCommentModalOpen.set(true);
  }

  onRejectCommentChange(value: string): void {
    this.rejectCommentValue.set(value);
  }

  onCancelRejectComment(): void {
    this.pendingRejectDetail = null;
    this.rejectCommentValue.set('');
    this.isRejectCommentModalOpen.set(false);
  }

  onConfirmRejectComment(): void {
    const detail = this.pendingRejectDetail;
    const comment = this.rejectCommentValue().trim();

    if (detail == null || detail.approvalIds.length === 0 || comment === '') {
      this.approvalActionErrorMessage.set('กรุณาระบุรายละเอียดที่ต้องแก้ไข');
      return;
    }

    this.submitCourseReviewDecision(detail, 'reject', comment);
  }

  onSubmitForTeacherReview(): void {
    const submissionId = this.scheduleSubmissionId;

    if (submissionId == null || !this.canSubmitForTeacherReview() || this.isSubmissionActionLoading()) {
      return;
    }

    this.pendingConfirmationAction.set('submit_teacher_review');
    this.isReviewConfirmModalOpen.set(true);
    this.workflowConfirmDialog?.open();
  }

  private confirmSubmitForTeacherReview(submissionId: number): void {
    this.isSubmissionActionLoading.set(true);
    this.submissionActionErrorMessage.set('');
    this.scheduleSubmissionsService.submitForTeacherReview(submissionId).subscribe({
      next: (response) => {
        this.applyScheduleSubmission(response);
        this.workflowSuccessTitle.set('ส่งตารางสอนสำเร็จ');
        this.reviewSuccessMessage.set('ส่งตารางสอนให้อาจารย์ตรวจสอบเรียบร้อยแล้ว');
        this.isSubmissionActionLoading.set(false);
        this.isReviewConfirmModalOpen.set(false);
        this.workflowConfirmDialog?.close();
        this.workflowSuccessDialog?.open();
        this.pendingConfirmationAction.set(null);
      },
      error: (err) => {
        this.submissionActionErrorMessage.set(this.extractErrorMessage(err));
        this.isSubmissionActionLoading.set(false);
        this.isReviewConfirmModalOpen.set(false);
        this.workflowConfirmDialog?.close();
        this.workflowErrorTitle.set('ไม่สามารถส่งตารางสอนได้');
        this.workflowErrorDialog?.open();
      },
    });
  }

  onOpenReviewConfirm(action: ConfirmationAction): void {
    if (this.isSubmissionActionLoading()) {
      return;
    }

    if (action === 'approve' && !this.canConfirmApprovalReview()) {
      return;
    }

    if (action === 'reject' && !this.canConfirmRejectReview()) {
      return;
    }

    this.pendingConfirmationAction.set(action);
    this.isReviewConfirmModalOpen.set(true);
    this.workflowConfirmDialog?.open();
  }

  onCancelReviewConfirm(): void {
    this.pendingConfirmationAction.set(null);
    this.isReviewConfirmModalOpen.set(false);
  }

  onConfirmReviewSubmission(): void {
    const submissionId = this.scheduleSubmissionId;
    const action = this.pendingConfirmationAction();

    if (submissionId == null || action == null || this.isSubmissionActionLoading()) {
      return;
    }

    if (action === 'reject' && !this.canConfirmRejectReview()) {
      return;
    }

    if (action === 'approve' && !this.canConfirmApprovalReview()) {
      return;
    }

    const confirmingAsEducationHead = this.isEducationHeadActionStage();
    const request$ = action === 'reject'
      ? this.scheduleSubmissionsService.notifyRevisionRequested(submissionId)
      : confirmingAsEducationHead
        ? this.scheduleSubmissionsService.educationHeadConfirm(submissionId)
        : this.scheduleSubmissionsService.teacherConfirm(submissionId);

    if (action === 'submit_teacher_review') {
      this.confirmSubmitForTeacherReview(submissionId);
      return;
    }

    if (action === 'approve' && confirmingAsEducationHead) {
      this.isEducationHeadConfirming.set(true);
    }

    this.isSubmissionActionLoading.set(true);
    this.submissionActionErrorMessage.set('');
    request$.subscribe({
      next: (response) => {
        this.applyScheduleSubmission(response);
        this.reviewSuccessMessage.set(this.getReviewSuccessMessage(response));
        this.workflowSuccessTitle.set(action === 'reject'
          ? 'ส่งอีเมลสำเร็จ'
          : confirmingAsEducationHead ? 'รับรองตารางสอนสำเร็จ' : 'ส่งตารางสอนสำเร็จ');
        this.isReviewSuccessModalOpen.set(true);
        this.pendingConfirmationAction.set(null);
        this.isReviewConfirmModalOpen.set(false);
        this.isSubmissionActionLoading.set(false);
        this.isEducationHeadConfirming.set(false);
        this.workflowConfirmDialog?.close();
        this.workflowSuccessDialog?.open();
      },
      error: (err) => {
        this.submissionActionErrorMessage.set(this.extractErrorMessage(err));
        this.isSubmissionActionLoading.set(false);
        this.isEducationHeadConfirming.set(false);
        this.isReviewConfirmModalOpen.set(false);
        this.workflowConfirmDialog?.close();
        this.workflowErrorTitle.set(action === 'reject' ? 'ไม่สามารถส่งอีเมลได้' : 'ไม่สามารถดำเนินการได้');
        this.workflowErrorDialog?.open();
      },
    });
  }

  onCloseReviewSuccessModal(): void {
    this.reviewSuccessMessage.set('');
    this.isReviewSuccessModalOpen.set(false);
    this.workflowSuccessDialog?.close();
  }

  onOpenCorrectionRequest(): void {
    if (!this.canRequestSpecialCorrection()) {
      return;
    }

    this.correctionNoteByKey.clear();
    this.correctionTouchedKeys.clear();
    this.correctionRows.set(this.buildCorrectionCourseRows());
    this.correctionSubmitAttempted.set(false);
    this.correctionErrorMessage.set('');
    this.isCorrectionModalOpen.set(true);
  }

  onCloseCorrectionRequest(): void {
    if (this.isCorrectionSubmitting()) {
      return;
    }

    this.isCorrectionModalOpen.set(false);
    this.isCorrectionSubmitConfirmModalOpen.set(false);
    this.correctionNoteByKey.clear();
    this.correctionTouchedKeys.clear();
    this.correctionSubmitAttempted.set(false);
    this.correctionErrorMessage.set('');
    this.showNewCourseRequestNote.set(false);
    this.newCourseRequestNoteControl.setValue('');
  }

  onShowNewCourseRequestNote(): void {
    this.showNewCourseRequestNote.set(true);
  }

  onToggleCorrectionCourse(row: CorrectionCourseRow, checked: boolean): void {
    this.correctionRows.update((rows) => rows.map((item) => (
      item.key === row.key
        ? { ...item, selected: checked, touched: item.touched || this.correctionTouchedKeys.has(item.key) || !checked }
        : item
    )));
  }

  onCorrectionCourseNoteChange(row: CorrectionCourseRow, event: Event): void {
    const value = (event.target as HTMLTextAreaElement | null)?.value ?? '';
    this.correctionNoteByKey.set(row.key, value);
    this.correctionTouchedKeys.add(row.key);
    row.teacherNote = value;
    row.touched = true;
  }

  trackCorrectionCourseRow(_index: number, row: CorrectionCourseRow): string {
    return row.key;
  }

  onSubmitCorrectionRequest(): void {
    const submissionId = this.scheduleSubmissionId;

    this.correctionSubmitAttempted.set(true);
    this.correctionErrorMessage.set('');

    if (submissionId == null || !this.isCorrectionRequestValid() || this.isCorrectionSubmitting()) {
      return;
    }

    this.isCorrectionSubmitConfirmModalOpen.set(true);
    this.correctionSubmitConfirmDialog?.open();
  }

  onCancelCorrectionSubmitConfirm(): void {
    if (this.isCorrectionSubmitting()) {
      return;
    }

    this.isCorrectionSubmitConfirmModalOpen.set(false);
  }

  onConfirmCorrectionSubmit(): void {
    const submissionId = this.scheduleSubmissionId;

    if (submissionId == null || !this.isCorrectionRequestValid() || this.isCorrectionSubmitting()) {
      return;
    }

    const selectedRows = this.selectedCorrectionRows();

    this.isCorrectionSubmitting.set(true);
    this.scheduleSubmissionsService.createCorrectionRequest(submissionId, {
      new_course_request_note: this.newCourseRequestNoteControl.value.trim() || null,
      items: selectedRows.map((row) => ({
        approvable_type: row.approvableType,
        approvable_id: row.approvableId,
        teacher_note: this.correctionTeacherNote(row).trim(),
      })),
    }).subscribe({
      next: (request) => {
        this.applyActiveCorrectionRequest(request);
        this.correctionRequestSubmitted.set(true);
        this.isCorrectionSubmitting.set(false);
        this.isCorrectionModalOpen.set(false);
        this.isCorrectionSubmitConfirmModalOpen.set(false);
        this.correctionSubmitConfirmDialog?.close();
        this.workflowSuccessTitle.set('ส่งคำขอแก้ไขสำเร็จ');
        this.reviewSuccessMessage.set('ส่งคำขอแก้ไขหลังการรับรองเรียบร้อยแล้ว');
        this.workflowSuccessDialog?.open();
      },
      error: (err) => {
        this.correctionErrorMessage.set(this.extractErrorMessage(err));
        this.isCorrectionSubmitting.set(false);
        this.isCorrectionSubmitConfirmModalOpen.set(false);
        this.correctionSubmitConfirmDialog?.close();
        this.workflowErrorTitle.set('ไม่สามารถส่งคำขอแก้ไขได้');
        this.submissionActionErrorMessage.set(this.correctionErrorMessage());
        this.workflowErrorDialog?.open();
      },
    });
  }

  canRequestSpecialCorrection(): boolean {
    return this.isAcademicTermSynced() &&
      !this.isViewingHistoricalVersion() &&
      !this.isApprovalReviewMode() &&
      this.isTeacherReviewRole() &&
      (this.isAdminRole() || this.isScheduleOwner()) &&
      this.isEducationHeadFinalConfirmed() &&
      this.scheduleSubmission()?.can_request_special_correction !== false &&
      !this.hasActiveCorrectionRequest();
  }

  hasActiveCorrectionRequest(): boolean {
    return this.correctionRequestSubmitted() ||
      this.scheduleSubmission()?.active_correction_request != null;
  }

  shouldShowSpecialCorrectionBanner(): boolean {
    return !this.isApprovalReviewMode() && this.hasActiveCorrectionRequest();
  }

  selectedCorrectionRows(): CorrectionCourseRow[] {
    return this.correctionRows().filter((row) => row.selected);
  }

  isCorrectionRequestValid(): boolean {
    const selectedRows = this.selectedCorrectionRows();
    const hasNewCourseNote = this.newCourseRequestNoteControl.value.trim() !== '';

    return (selectedRows.length > 0 || hasNewCourseNote) &&
      selectedRows.every((row) => this.correctionTeacherNote(row).trim() !== '');
  }

  shouldShowCorrectionSelectionError(): boolean {
    return this.correctionSubmitAttempted() &&
      this.selectedCorrectionRows().length === 0 &&
      this.newCourseRequestNoteControl.value.trim() === '';
  }

  shouldShowCorrectionCourseNoteError(row: CorrectionCourseRow): boolean {
    return row.selected &&
      (row.touched || this.correctionTouchedKeys.has(row.key) || this.correctionSubmitAttempted()) &&
      this.correctionTeacherNote(row).trim() === '';
  }

  correctionTeacherNote(row: CorrectionCourseRow): string {
    return this.correctionNoteByKey.get(row.key) ?? row.teacherNote;
  }

  canApproveSelectedCourse(): boolean {
    if (!this.isAcademicTermSynced() ||
        this.isViewingHistoricalVersion() ||
        !this.isApprovalReviewMode() ||
        this.isTeacherReviewLocked()) {
      return false;
    }

    if (this.isEducationHeadDeadlineBlocked()) {
      return false;
    }

    const detail = this.selectedCourseDetail();
    return this.getApproveTargetStatus(detail) != null && !this.hasSharedWorkloadRevisionConflict(detail);
  }

  isEducationHeadDeadlineBlocked(): boolean {
    return this.isAcademicTermSynced() &&
      !this.isViewingHistoricalVersion() &&
      this.isApprovalReviewMode() &&
      !this.isTeacherReviewLocked() &&
      this.isEducationHeadActionStage() &&
      this.scheduleSubmission()?.can_education_head_confirm === false;
  }

  isSelectedCourseApprovalDeadlineBlocked(): boolean {
    if (!this.isEducationHeadDeadlineBlocked()) {
      return false;
    }

    const detail = this.selectedCourseDetail();
    return this.getApproveTargetStatus(detail) != null && !this.hasSharedWorkloadRevisionConflict(detail);
  }

  getEducationHeadDeadlineBlockedReason(): string {
    return this.isEducationHeadDeadlineBlocked() ? EDUCATION_HEAD_DEADLINE_BLOCKED_REASON : '';
  }

  canRejectSelectedCourse(): boolean {
    if (!this.isAcademicTermSynced() ||
        this.isViewingHistoricalVersion() ||
        !this.isApprovalReviewMode() ||
        this.isTeacherReviewLocked()) {
      return false;
    }

    const detail = this.selectedCourseDetail();

    if (detail == null || detail.approvalIds.length === 0) {
      return false;
    }

    if (this.isAdminRole()) {
      return detail.actionApprovalStatusCode === 'ready_for_teacher' ||
        detail.actionApprovalStatusCode === 'teacher_confirmed';
    }

    if (this.isTeacherReviewRole()) {
      return detail.actionApprovalStatusCode === 'ready_for_teacher';
    }

    if (this.isEducationHeadRole()) {
      return detail.actionApprovalStatusCode === 'teacher_confirmed';
    }

    return false;
  }

  sharedWorkloadCorrectionMessage(): string {
    return this.hasSharedWorkloadRevisionConflict(this.selectedCourseDetail())
      ? 'รายวิชานี้มีอาจารย์ร่วมสอนส่งกลับแก้ไขแล้ว สามารถระบุความคิดเห็นเพิ่มเติมได้ แต่ยังไม่สามารถรับรองได้จนกว่าเจ้าหน้าที่จะแก้ไขและส่งกลับมาใหม่'
      : '';
  }

  canSubmitForTeacherReview(): boolean {
    return this.isAcademicTermSynced() &&
      !this.isViewingHistoricalVersion() &&
      this.isAcademicWorkloadManagerRole() &&
      this.allApprovalStatuses().some((status) => (
        status === 'imported_pending_fix' ||
        status === 'special_revision_requested' ||
        status === 'revision_requested'
      ));
  }

  canReviewAssignments(): boolean {
    if (!this.isAcademicTermSynced() ||
        this.isViewingHistoricalVersion() ||
        !this.isApprovalReviewMode()) {
      return false;
    }

    return this.isTeacherReviewRole() || this.isEducationHeadRole();
  }

  isApprovalReviewMode(): boolean {
    return this.route.snapshot.data['approvalReview'] === true;
  }

  isStaffNormalOverview(): boolean {
    return !this.isApprovalReviewMode() &&
      (this.isAcademicWorkloadManagerRole() || this.isEducationHeadRole());
  }

  canOpenSubjectManagementDetail(): boolean {
    const detail = this.selectedCourseDetail();
    const subjectId = detail?.subjectId;

    return this.isAcademicTermSynced() &&
      (this.isStaffNormalOverview() || (this.isApprovalReviewMode() && this.isAdminRole())) &&
      subjectId != null &&
      Number.isFinite(subjectId) &&
      subjectId > 0;
  }

  isAcademicTermSynced(): boolean {
    return this.scheduleSubmission()?.academic_term?.sync_at != null;
  }

  openSubjectManagementDetail(): void {
    const detail = this.selectedCourseDetail();

    if (!this.canOpenSubjectManagementDetail() || detail?.subjectId == null) {
      return;
    }

    const correctionRequest = this.scheduleSubmission()?.active_correction_request;
    const correctionRequestId = correctionRequest?.status === 'editing' &&
      Number.isFinite(Number(correctionRequest.id)) &&
      Number(correctionRequest.id) > 0
      ? Number(correctionRequest.id)
      : null;
    const urlTree = this.router.createUrlTree(['/subject-management/details'], {
      queryParams: {
        id: detail.subjectId,
        type: detail.type,
        ...(correctionRequestId == null ? {} : { correction_request_id: correctionRequestId }),
      },
    });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isViewingHistoricalVersion(): boolean {
    if (!this.isScheduleVersionViewerEnabled) {
      return false;
    }

    const selectedVersion = this.selectedVersion();
    return selectedVersion != null && !selectedVersion.is_current && this.selectedVersionDetail() != null;
  }

  selectedVersion(): ScheduleSubmissionVersionSummary | null {
    const selectedId = this.selectedVersionId();
    return this.scheduleVersions().find((version) => version.id === selectedId) ?? null;
  }

  versionSelectLabel(version: ScheduleSubmissionVersionSummary): string {
    const parts = [this.versionPrimaryLabel(version)];
    const sourceLabel = String(version.source_label ?? '').trim();
    const approvedAt = this.formatDateTime(version.approved_at);

    if (version.is_current) {
      parts.push('ปัจจุบัน');
    }

    if (sourceLabel !== '') {
      parts.push(sourceLabel);
    }

    if (approvedAt !== '-') {
      parts.push(approvedAt);
    }

    return parts.join(' — ');
  }

  versionPrimaryLabel(version: ScheduleSubmissionVersionSummary): string {
    return version.is_current
      ? `ข้อมูลปัจจุบัน / Version ${version.version_no}`
      : `Version ${version.version_no}`;
  }

  versionMetadataLabel(version: ScheduleSubmissionVersionSummary): string {
    const parts: string[] = [];
    const sourceLabel = String(version.source_label ?? '').trim();
    const approvedAt = this.formatDateTime(version.approved_at);

    if (sourceLabel !== '') {
      parts.push(sourceLabel);
    }

    if (approvedAt !== '-') {
      parts.push(approvedAt);
    }

    return parts.length > 0 ? parts.join(' · ') : 'ไม่มีข้อมูลเพิ่มเติม';
  }

  pageTitle(): string {
    return this.isApprovalReviewMode()
      ? 'การจัดการตารางสอน - ตรวจสอบและรับรองตารางสอน'
      : 'การจัดการตารางสอน - ตารางสอน';
  }

  canConfirmRejectReview(): boolean {
    if (this.isTeacherReviewLocked()) {
      return false;
    }

    if (this.scheduleSubmission()?.staff_revision_notification?.already_sent_for_current_set === true) {
      return false;
    }

    const statuses = this.allApprovalStatuses();
    const decidedStatuses = this.isEducationHeadActionStage()
      ? [
        'special_revision_requested',
        'revision_requested',
        'education_head_confirmed',
        'vice_dean_confirmed',
        'locked',
      ]
      : [
        'special_revision_requested',
        'revision_requested',
        'teacher_confirmed',
        'education_head_confirmed',
        'vice_dean_confirmed',
        'locked',
      ];

    return statuses.some((status) => (
      status === 'special_revision_requested' ||
      status === 'revision_requested'
    )) &&
      statuses.every((status) => decidedStatuses.includes(status));
  }

  canConfirmApprovalReview(): boolean {
    if (this.isTeacherReviewLocked()) {
      return false;
    }

    const statuses = this.allApprovalStatuses();

    if (statuses.length === 0 && !this.isInSpecialCorrectionReviewCycle()) {
      return false;
    }

    if (this.isAdminRole()) {
      return this.isEducationHeadActionStage()
        ? this.canConfirmAsEducationHead(statuses)
        : this.canConfirmAsTeacher(statuses);
    }

    if (this.isTeacherReviewRole()) {
      return this.canConfirmAsTeacher(statuses);
    }

    if (this.isEducationHeadRole()) {
      return this.canConfirmAsEducationHead(statuses);
    }

    return false;
  }

  private canConfirmAsTeacher(statuses: string[]): boolean {
    if (this.isFinalSubmitBlockedByPendingSpecialChange()) {
      return false;
    }

    if (this.isTeacherSubmittedToEducationHead()) {
      return false;
    }

    return statuses.every((status) => [
      'teacher_confirmed',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
    ].includes(status));
  }

  isTeacherReviewLocked(): boolean {
    return this.isTeacherReviewRole() &&
      this.scheduleSubmission()?.released_for_teacher_review !== true;
  }

  private isInSpecialCorrectionReviewCycle(): boolean {
    return SPECIAL_CORRECTION_REVIEW_CYCLE_STATUSES.includes(
      this.scheduleSubmission()?.active_correction_request?.status ?? '',
    );
  }

  private canConfirmAsEducationHead(statuses: string[]): boolean {
    if (this.isEducationHeadConfirming() || this.isEducationHeadFinalConfirmed()) {
      return false;
    }

    if (this.scheduleSubmission()?.can_education_head_confirm === false) {
      return false;
    }

    return this.isTeacherSubmittedToEducationHead() &&
      !this.hasRejectedOrRevisionItem() &&
      statuses.every((status) => [
        'teacher_confirmed',
        'education_head_confirmed',
        'vice_dean_confirmed',
        'locked',
      ].includes(status));
  }

  isFinalSubmitBlockedByPendingSpecialChange(): boolean {
    const correctionRequest = this.scheduleSubmission()?.active_correction_request;
    const hasSelectedCorrectionCourses =
      (correctionRequest?.affected_subject_schedule_group_ids?.length ?? 0) > 0 ||
      (correctionRequest?.affected_project_research_subject_assignment_ids?.length ?? 0) > 0;

    return this.isTeacherReviewRole() &&
      this.getScheduleDisplayStatusCode() === 'special_revision_requested' &&
      correctionRequest?.status === 'editing' &&
      !hasSelectedCorrectionCourses &&
      !this.allScheduleDisplayStatuses().some((status) => status === 'ready_for_teacher');
  }

  finalSubmitBlockedMessage(): string {
    return 'ยังไม่สามารถส่งขอรับรองได้ เนื่องจากตารางสอนยังไม่มีการเพิ่มหรือแก้ไขข้อมูลตามคำขอกรณีพิเศษ';
  }

  isWaitingForEducationHeadReview(): boolean {
    return this.isApprovalReviewMode() &&
      this.isTeacherReviewRole() &&
      this.isTeacherSubmittedToEducationHead();
  }



  isEducationHeadFinalConfirmed(): boolean {
    return this.scheduleSubmission()?.status === 'approved';
  }

  getReviewConfirmTitle(): string {
    if (this.pendingConfirmationAction() === 'submit_teacher_review') {
      return 'ส่งตารางสอนให้อาจารย์ตรวจสอบ';
    }

    return this.pendingConfirmationAction() === 'reject'
      ? 'ต้องการส่งอีเมลแจ้งเจ้าหน้าที่ใช่หรือไม่ ?'
      : this.isEducationHeadActionStage()
        ? 'ต้องการยืนยันการรับรองตารางสอนนี้ใช่หรือไม่ ?'
        : 'ต้องการส่งตารางสอนให้หัวหน้างานการศึกษาพิจารณาใช่หรือไม่ ?';
  }

  getReviewConfirmMessage(): string {
    if (this.pendingConfirmationAction() === 'submit_teacher_review') {
      return 'ต้องการส่งตารางสอนที่แก้ไขแล้วให้อาจารย์ตรวจสอบใช่หรือไม่ ?';
    }

    return this.pendingConfirmationAction() === 'reject'
      ? 'ระบบจะส่งอีเมลแจ้งเจ้าหน้าที่พร้อมรายการวิชาที่ต้องแก้ไข'
      : this.isEducationHeadActionStage()
        ? 'การดำเนินการนี้จะรับรอง ตารางสอนทั้งหมดของอาจารย์ท่านนี้ ต้องการดำเนินการต่อหรือไม่'
        : 'ระบบจะส่งตารางสอนเข้าสู่ขั้นตอนพิจารณาของหัวหน้างานการศึกษา';
  }

  getConfirmApprovalButtonLabel(): string {
    if (this.isEducationHeadActionStage()) {
      return 'ยืนยันการรับรอง';
    }

    if (this.isAdminRole()) {
      return 'ส่งพิจารณารับรอง';
    }

    return this.isWaitingForEducationHeadReview()
      ? 'ส่งให้หัวหน้างานการศึกษาแล้ว'
      : 'ส่งให้หัวหน้างานการศึกษา';
  }



  getScheduleStatusClass(): string {
    const statusCode = this.getScheduleListStatusSource();
    const normalized = normalizeScheduleSubmissionListStatusCode(statusCode);

    switch (normalized) {
      case 'draft':
      case 'imported_pending_fix':
        return 'schedule-submissions-status-unknown'; // gray draft/unknown
      case 'ready_for_teacher':
      case 'waiting_teacher_submit':
        return 'schedule-submissions-status-ready-for-teacher'; // yellow
      case 'teacher_confirmed':
      case 'waiting_education_head_review':
        return 'schedule-submissions-status-waiting-education-head-review'; // blue
      case 'revision_requested':
        return 'schedule-submissions-status-revision-requested'; // orange
      case 'special_revision_requested':
        return 'schedule-submissions-status-special-revision-requested'; // red
      case 'approved':
      case 'education_head_confirmed':
      case 'vice_dean_confirmed':
      case 'locked':
        return 'schedule-submissions-status-education-head-confirmed'; // green
      default:
        return 'schedule-submissions-status-unknown'; // fallback (gray)
    }
  }

  getScheduleStatusLabel(): string {
    return getScheduleSubmissionListStatusDisplay(this.getScheduleListStatusSource()).label;
  }

  getStatusClass(status: CourseDisplayStatusCode | null | undefined): string {
    const normalizedStatus = this.normalizeCourseDisplayStatusCode(status);
    let visualStatus: CourseDisplayStatusCode = normalizedStatus;

    if (normalizedStatus === 'draft') {
      visualStatus = 'imported_pending_fix';
    }

    if (
      normalizedStatus === 'teacher_confirmed' &&
      !this.isAdminRole() &&
      !this.isEducationHeadActionStage()
    ) {
      visualStatus = 'education_head_confirmed';
    }

    if (normalizedStatus === 'waiting_teacher_submit') {
      visualStatus = 'ready_for_teacher';
    }

    if (this.isAdminRole() && normalizedStatus === 'waiting_co_teacher') {
      visualStatus = 'ready_for_teacher';
    }

    return `schedule-submissions-status-${visualStatus.replace(/_/g, '-')}`;
  }

  getTimetableEventClass(event: Pick<TimetableEvent, 'statusCode'>): string {
    if (this.isStaffNormalOverview()) {
      return this.getStaffOverviewCourseStatusClass(event.statusCode);
    }

    if (!this.isApprovalReviewMode()) {
      return 'schedule-submissions-timetable-event-neutral';
    }
    return this.getStatusClass(event.statusCode);
  }

  getCourseDisplayStatusClass(status: CourseDisplayStatusCode): string {
    return this.isStaffNormalOverview()
      ? this.getStaffOverviewCourseStatusClass(status)
      : this.getStatusClass(status);
  }

  getStaffOverviewCourseDisplayStatus(block: ScheduleSubmissionBlockModel): CourseDisplayStatusCode {
    if (this.isCourseInActiveCorrection(block)) {
      return 'special_revision_requested';
    }

    const rawStatus = block.approval?.status_code ??
      block.approval?.current_status?.code ??
      block.status;
    const normalizedStatus = normalizeScheduleSubmissionListStatusCode(rawStatus);

    if (normalizedStatus === 'special_revision_requested' || normalizedStatus === 'revision_requested') {
      return 'revision_requested';
    }

    if (normalizedStatus === 'teacher_confirmed' || normalizedStatus === 'waiting_education_head_review') {
      return 'waiting_education_head_review';
    }

    if (normalizedStatus === 'ready_for_teacher' || normalizedStatus === 'waiting_teacher_submit') {
      return 'ready_for_teacher';
    }

    if ([
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
    ].includes(normalizedStatus)) {
      return 'education_head_confirmed';
    }

    return 'imported_pending_fix';
  }

  getStaffOverviewCourseStatusLabel(status: CourseDisplayStatusCode): string {
    return getScheduleSubmissionListStatusDisplay(status).label;
  }

  getStaffOverviewCourseStatusClass(status: CourseDisplayStatusCode): string {
    const normalizedStatus = normalizeScheduleSubmissionListStatusCode(status);
    const visualStatusByCode: Record<string, CourseDisplayStatusCode> = {
      special_revision_requested: 'special_revision_requested',
      revision_requested: 'revision_requested',
      teacher_confirmed: 'waiting_education_head_review',
      waiting_education_head_review: 'waiting_education_head_review',
      ready_for_teacher: 'ready_for_teacher',
      waiting_teacher_submit: 'ready_for_teacher',
      education_head_confirmed: 'education_head_confirmed',
      vice_dean_confirmed: 'education_head_confirmed',
      locked: 'education_head_confirmed',
    };
    const visualStatus = visualStatusByCode[normalizedStatus] ?? 'imported_pending_fix';

    return `schedule-submissions-status-${visualStatus.replace(/_/g, '-')}`;
  }

  isCourseInActiveCorrection(block: ScheduleSubmissionBlockModel): boolean {
    const request = this.scheduleSubmission()?.active_correction_request;

    if (request == null) {
      return false;
    }

    if (block.type === 'subject_schedule') {
      return (request.affected_subject_schedule_group_ids ?? [])
        .includes(block.subject_schedule_group_id);
    }

    return (request.affected_project_research_subject_assignment_ids ?? [])
      .includes(block.project_research_subject_assignment_id);
  }

  getStatusHistoryClass(
    status: StatusHistoryDisplayCode | null | undefined,
    label?: string | null,
  ): string {
    if (String(label ?? '').trim() === 'รอพิจารณารับรอง') {
      return 'schedule-submissions-status-waiting-education-head-review';
    }

    const normalizedStatus = String(status ?? '').trim();
    const correctionClassByStatus: Record<CorrectionRequestHistoryStatusCode, string> = {
      correction_requested: 'schedule-submissions-status-ready-for-teacher',
      correction_approved: 'schedule-submissions-status-education-head-confirmed',
      correction_rejected: 'schedule-submissions-status-revision-requested',
      correction_opened: 'schedule-submissions-status-waiting-education-head-review',
      correction_completed: 'schedule-submissions-status-education-head-confirmed',
    };

    if (normalizedStatus in correctionClassByStatus) {
      return correctionClassByStatus[normalizedStatus as CorrectionRequestHistoryStatusCode];
    }

    return this.getStatusClass(status as CourseDisplayStatusCode);
  }

  getStatusLabel(status: CourseDisplayStatusCode | null | undefined): string {
    const normalizedStatus = this.normalizeCourseDisplayStatusCode(status);
    const labels: Record<CourseDisplayStatusCode, string> = {
      draft: 'แบบร่าง',
      imported_pending_fix: 'แบบร่าง',
      ready_for_teacher: 'รอดำเนินการ',
      waiting_co_teacher: 'รออาจารย์ผู้สอนร่วมรับรอง',
      waiting_teacher_submit: 'รอดำเนินการ',
      waiting_education_head_review: 'รอพิจารณารับรอง',
      teacher_confirmed: this.isAdminRole()
        ? 'รอพิจารณารับรอง'
        : this.isEducationHeadActionStage() ? 'รอดำเนินการ' : 'รับรองแล้ว',
      education_head_confirmed: 'รับรองแล้ว',
      vice_dean_confirmed: 'รับรองแล้ว',
      locked: 'รับรองแล้ว',
      special_revision_requested: 'ส่งกลับแก้ไขพิเศษ',
      revision_requested: 'ส่งกลับแก้ไข',
      unknown: '-',
    };

    return labels[normalizedStatus];
  }

  getTeacherApprovalStatusClass(status: ApprovalStatusCode | null | undefined): string {
    const normalizedStatus = this.normalizeApprovalStatusCode(status);
    const visualStatus = normalizedStatus === 'draft'
      ? 'imported_pending_fix'
      : normalizedStatus === 'teacher_confirmed'
        ? 'waiting-education-head-review'
        : normalizedStatus;

    return `schedule-submissions-status-${visualStatus.replace(/_/g, '-')}`;
  }
  allApprovalStatuses(): ApprovalStatusCode[] {
    return this.scheduleSubmission()?.blocks.map((block) => this.getBlockApprovalStatusCode(block)) ?? [];
  }

  approvalStatusSummaries(): ApprovalStatusSummary[] {
    const summaryByStatus = new Map<CourseDisplayStatusCode, ApprovalStatusSummary>();

    this.allDisplayApprovalStatuses().forEach((status) => {
      const existingSummary = summaryByStatus.get(status);

      if (existingSummary != null) {
        existingSummary.count += 1;
        return;
      }

      summaryByStatus.set(status, {
        status,
        label: this.getStatusLabel(status),
        count: 1,
      });
    });

    return Array.from(summaryByStatus.values());
  }

  private isTeacherSubmittedToEducationHead(): boolean {
    const submission = this.scheduleSubmission();
    return submission?.teacher_submitted_to_education_head === true ||
      submission?.teacher_submitted_at != null;
  }

  private openCourseDetail(target: CourseDetailTarget): void {
    const scheduleSubmissionId = this.scheduleSubmissionId;
    const requestId = this.courseDetailRequestId + 1;

    this.courseDetailRequestId = requestId;
    this.isCourseDetailModalOpen.set(true);
    this.isCourseDetailLoading.set(true);
    this.courseDetailErrorMessage.set('');
    this.selectedCourseDetail.set(null);

    if (scheduleSubmissionId == null) {
      this.showCourseDetailLoadError(requestId);
      return;
    }

    if (this.isViewingHistoricalVersion()) {
      this.loadSnapshotCourseDetail(target, requestId);
      return;
    }

    if (target.type === 'subject_schedule') {
      this.loadSubjectScheduleCourseDetail(scheduleSubmissionId, target.subjectScheduleId, requestId);
      return;
    }

    this.loadProjectResearchCourseDetail(scheduleSubmissionId, target.projectResearchSubjectId, requestId);
  }

  private submitCourseReviewDecision(
    detail: CourseDetail,
    action: CourseReviewAction,
    comment: string | null = null
  ): void {
    const submissionId = this.scheduleSubmissionId;

    if (submissionId == null || detail.approvalIds.length === 0 || this.isSubmissionActionLoading()) {
      return;
    }

    this.isSubmissionActionLoading.set(true);
    this.approvalActionErrorMessage.set('');
    this.submissionActionErrorMessage.set('');

    this.scheduleSubmissionsService.reviewAssignments(
      submissionId,
      detail.approvalIds.map((approvalRequestId) => ({
        approval_request_id: approvalRequestId,
        action,
        comment,
      })),
    ).subscribe({
      next: (response) => {
        this.applyScheduleSubmission(response);
        this.pendingApproveDetail = null;
        this.pendingRejectDetail = null;
        this.rejectCommentValue.set('');
        this.isCourseApproveConfirmModalOpen.set(false);
        this.isRejectCommentModalOpen.set(false);
        this.isSubmissionActionLoading.set(false);
        this.onCloseCourseDetail();
        this.courseApproveConfirmDialog?.close();
        this.workflowSuccessTitle.set(action === 'approve' ? 'รับรองรายวิชาสำเร็จ' : 'ส่งกลับแก้ไขสำเร็จ');
        this.reviewSuccessMessage.set(action === 'approve' ? 'รับรองรายวิชาเรียบร้อยแล้ว' : 'ส่งรายวิชากลับแก้ไขเรียบร้อยแล้ว');
        this.workflowSuccessDialog?.open();
      },
      error: (err) => {
        this.approvalActionErrorMessage.set(this.extractErrorMessage(err));
        this.isSubmissionActionLoading.set(false);
        this.isCourseApproveConfirmModalOpen.set(false);
        this.courseApproveConfirmDialog?.close();
        this.workflowErrorTitle.set(action === 'approve' ? 'ไม่สามารถรับรองรายวิชาได้' : 'ไม่สามารถส่งกลับแก้ไขได้');
        this.submissionActionErrorMessage.set(this.approvalActionErrorMessage());
        this.workflowErrorDialog?.open();
      },
    });
  }

  private getApproveTargetStatus(detail: CourseDetail | null): string | null {
    if (detail == null || detail.approvalIds.length === 0) {
      return null;
    }

    if (
      this.isAdminRole() &&
      this.isTeacherSubmittedToEducationHead() &&
      detail.actionApprovalStatusCode === 'teacher_confirmed'
    ) {
      return 'education_head_confirmed';
    }

    if (this.isTeacherReviewRole() && detail.actionApprovalStatusCode === 'ready_for_teacher') {
      return 'teacher_confirmed';
    }

    if (this.isEducationHeadActionStage() && detail.actionApprovalStatusCode === 'teacher_confirmed') {
      return 'education_head_confirmed';
    }

    return null;
  }

  private hasSharedWorkloadRevisionConflict(detail: CourseDetail | null): boolean {
    if (detail?.type !== 'subject_schedule' || detail.teachers.length <= 1) {
      return false;
    }

    return detail.actionApprovalStatusCode === 'ready_for_teacher' &&
      detail.teachers.some((teacher) => (
        teacher.statusCode === 'special_revision_requested' ||
        teacher.statusCode === 'revision_requested'
      ));
  }

  private getActiveRoleId(): RoleID | null {
    const roleId = this.authService.role();
    return roleId == null ? null : Number(roleId);
  }

  private isAcademicWorkloadManagerRole(): boolean {
    return [RoleID.ADMIN, RoleID.ACADEMIC_OFFICER].includes(this.getActiveRoleId() as RoleID);
  }

  private isEducationHeadRole(): boolean {
    return this.getActiveRoleId() === RoleID.HEAD_OF_EDUCATION;
  }

  private isAdminRole(): boolean {
    return this.getActiveRoleId() === RoleID.ADMIN;
  }

  private isEducationHeadActionStage(): boolean {
    return this.isEducationHeadRole() ||
      (this.isAdminRole() && this.isTeacherSubmittedToEducationHead());
  }

  private isTeacherReviewRole(): boolean {
    const roleId = this.getActiveRoleId();
    if (roleId == null) {
      return false;
    }

    return [
      RoleID.TEACHER,
      RoleID.HEAD_OF_DEPARTMENT,
      RoleID.VICE_DEAN_ADMINISTRATION,
      RoleID.VICE_DEAN_ACADEMIC,
      RoleID.ADMIN,
    ].includes(roleId);
  }

  private isScheduleOwner(): boolean {
    const currentUser = this.authService.currentUser() ?? this.authService.currentUserValue;
    const currentUserId = Number(currentUser?.user.id);
    const scheduleTeacherId = Number(this.scheduleSubmission()?.teacher.id);

    return Number.isFinite(currentUserId) &&
      Number.isFinite(scheduleTeacherId) &&
      currentUserId === scheduleTeacherId;
  }

  private buildCorrectionCourseRows(): CorrectionCourseRow[] {
    const blocks = this.scheduleSubmission()?.blocks ?? [];
    return [
      ...this.buildSubjectScheduleCorrectionRows(blocks),
      ...this.buildProjectResearchCorrectionRows(blocks),
    ];
  }

  private buildSubjectScheduleCorrectionRows(blocks: ScheduleSubmissionBlockModel[]): CorrectionCourseRow[] {
    const groupedBlocks = new Map<number, SubjectScheduleBlockModel[]>();

    blocks
      .filter((block): block is SubjectScheduleBlockModel => block.type === 'subject_schedule')
      .filter((block) => this.isApprovedCorrectionCourse(block))
      .forEach((block) => {
        const assignmentId = Number(block.schedule_submission_subject_schedule_id);
        if (!Number.isFinite(assignmentId)) {
          return;
        }

        groupedBlocks.set(assignmentId, [...(groupedBlocks.get(assignmentId) ?? []), block]);
      });

    return Array.from(groupedBlocks.entries()).map(([assignmentId, subjectBlocks]) => {
      const sortedBlocks = this.sortSubjectScheduleBlocks(subjectBlocks);
      const primaryBlock = sortedBlocks[0];

      return {
        key: `subject_schedule:${assignmentId}`,
        approvableType: 'ScheduleSubmissionSubjectSchedule',
        approvableId: assignmentId,
        subjectCode: primaryBlock.subject.code,
        subjectName: primaryBlock.subject.name,
        section: this.formatDisplayValue(primaryBlock.section),
        teachingType: this.joinUnique(sortedBlocks.map((block) => this.subjectScheduleTeachingTypeLabel(block))),
        teachingDays: this.formatSubjectScheduleTeachingDays(sortedBlocks),
        teachingTimes: this.formatSubjectScheduleTeachingTimes(sortedBlocks),
        roomName: this.joinUnique(
          sortedBlocks
            .map((block) => this.formatDisplayValue(block.time.room_name))
            .filter((roomName) => roomName !== '-')
        ),
        workloadSplitLabel: this.workloadSplitLabel(primaryBlock.time.is_workload_split),
        statusCode: this.getBlockDisplayStatusCode(primaryBlock),
        statusLabel: this.getBlockDisplayStatusLabel(primaryBlock),
        selected: false,
        teacherNote: '',
        touched: false,
      };
    });
  }

  private buildProjectResearchCorrectionRows(blocks: ScheduleSubmissionBlockModel[]): CorrectionCourseRow[] {
    return blocks
      .filter((block): block is ProjectResearchSubjectBlockModel => block.type === 'project_research_subject')
      .filter((block) => this.isApprovedCorrectionCourse(block))
      .map((block) => ({
        key: `project_research_subject:${block.project_research_subject_assignment_id}`,
        approvableType: 'ProjectResearchSubjectAssignment' as const,
        approvableId: block.project_research_subject_assignment_id,
        subjectCode: block.subject.code,
        subjectName: block.subject.name,
        section: this.formatDisplayValue(block.section),
        teachingType: 'รายวิชาโครงงาน / วิจัย',
        teachingDays: this.formatTeachingDays(block.teaching_times),
        teachingTimes: this.formatTeachingTimes(block.teaching_times),
        roomName: '-',
        workloadSplitLabel: this.workloadSplitLabel(false),
        statusCode: this.getBlockDisplayStatusCode(block),
        statusLabel: this.getBlockDisplayStatusLabel(block),
        selected: false,
        teacherNote: '',
        touched: false,
      }));
  }

  private isApprovedCorrectionCourse(block: ScheduleSubmissionBlockModel): boolean {
    return [
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
    ].includes(this.getBlockActionApprovalStatusCode(block));
  }

  private applyActiveCorrectionRequest(request: ScheduleSubmissionCorrectionRequestModel): void {
    const submission = this.scheduleSubmission();

    if (submission == null) {
      return;
    }

    this.scheduleSubmission.set({
      ...submission,
      active_correction_request: request,
    });
  }



  private getBlockApprovalStatusCode(block: ScheduleSubmissionBlockModel): ApprovalStatusCode {
    return this.getSavedBlockApprovalStatusCode(block);
  }

  private getScheduleDisplayStatusCode(): CourseDisplayStatusCode {
    const submission = this.scheduleSubmission();
    const statuses = this.allScheduleDisplayStatuses();
    const apiStatus = this.normalizeScheduleStatusCode(
      submission?.approval_status?.status_code ?? submission?.status
    );

    if (apiStatus === 'special_revision_requested') {
      return apiStatus;
    }

    if (statuses.includes('special_revision_requested')) {
      return 'special_revision_requested';
    }

    if (statuses.includes('revision_requested')) {
      return 'revision_requested';
    }

    if (statuses.length > 0 && statuses.every((status) => [
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
    ].includes(status))) {
      return submission?.status === 'approved'
        ? 'education_head_confirmed'
        : 'waiting_education_head_review';
    }

    if (this.isTeacherSubmittedToEducationHead()) {
      return 'waiting_education_head_review';
    }

    if (statuses.some((status) => this.isPendingScheduleDisplayStatus(status))) {
      return 'ready_for_teacher';
    }

    if (statuses.length > 0 && statuses.every((status) => [
      'teacher_confirmed',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
    ].includes(status))) {
      return 'waiting_teacher_submit';
    }

    if (apiStatus !== 'unknown') {
      return apiStatus;
    }

    return statuses.length > 0 ? 'ready_for_teacher' : 'unknown';
  }

  private allDisplayApprovalStatuses(): CourseDisplayStatusCode[] {
    return this.scheduleSubmission()?.blocks.map((block) => this.getBlockDisplayStatusCode(block)) ?? [];
  }

  private getBlockDisplayStatusCode(block: ScheduleSubmissionBlockModel): CourseDisplayStatusCode {
    if (this.isStaffNormalOverview()) {
      return this.getStaffOverviewCourseDisplayStatus(block);
    }

    const status = this.isAdminRole()
      ? this.getBlockActionApprovalStatusCode(block)
      : this.getBlockApprovalStatusCode(block);

    if (
      this.isAdminRole() &&
      this.isWaitingForSharedTeachers(block, this.getSavedBlockApprovalStatusCode(block))
    ) {
      return 'waiting_co_teacher';
    }

    if (this.isTeacherReviewRole() && this.isWaitingForSharedTeachers(block, status)) {
      return 'waiting_co_teacher';
    }

    if (!this.isAdminRole() && this.isEducationHeadActionStage() && status === 'teacher_confirmed') {
      return 'ready_for_teacher';
    }

    return status;
  }

  private getSavedBlockApprovalStatusCode(block: ScheduleSubmissionBlockModel): ApprovalStatusCode {
    return this.normalizeApprovalStatusCode(
      block.approval?.status_code ??
      block.approval?.current_status?.code ??
      block.status
    );
  }

  private getBlockDisplayStatusLabel(block: ScheduleSubmissionBlockModel): string {
    const status = this.getBlockDisplayStatusCode(block);
    return this.isStaffNormalOverview()
      ? this.getStaffOverviewCourseStatusLabel(status)
      : this.getStatusLabel(status);
  }

  private getBlockApprovalId(block: ScheduleSubmissionBlockModel): number | null {
    return block.approval?.id ?? null;
  }

  private getBlockActionApprovalStatusCode(block: ScheduleSubmissionBlockModel): ApprovalStatusCode {
    if (block.type === 'subject_schedule') {
      return this.normalizeApprovalStatusCode(
        block.assignment_approval?.status_code ??
        block.assignment_approval?.current_status?.code ??
        block.approval?.status_code ??
        block.approval?.current_status?.code ??
        block.status
      );
    }

    return this.getSavedBlockApprovalStatusCode(block);
  }

  private getProjectResearchGroupStatus(blocks: ProjectResearchSubjectBlockModel[]): ApprovalStatusCode {
    const statuses = blocks.map((block) => this.getBlockApprovalStatusCode(block));
    const priority: ApprovalStatusCode[] = [
      'special_revision_requested',
      'revision_requested',
      'draft',
      'imported_pending_fix',
      'ready_for_teacher',
      'teacher_confirmed',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
      'unknown',
    ];

    return priority.find((status) => statuses.includes(status)) ?? 'unknown';
  }

  private getProjectResearchGroupStatusLabel(blocks: ProjectResearchSubjectBlockModel[]): string {
    return this.getStatusLabel(this.getProjectResearchGroupStatus(blocks));
  }

  private isWaitingForSharedTeachers(
    block: ScheduleSubmissionBlockModel,
    savedStatus: ApprovalStatusCode = this.getBlockApprovalStatusCode(block),
  ): boolean {
    if (block.type !== 'subject_schedule' || savedStatus !== 'ready_for_teacher') {
      return false;
    }

    const actionStatus = this.getBlockActionApprovalStatusCode(block);
    return block.time.is_workload_split &&
      [
        'teacher_confirmed',
        'education_head_confirmed',
        'vice_dean_confirmed',
        'locked',
      ].includes(actionStatus);
  }

  private normalizeApprovalStatusCode(status: string | null | undefined): ApprovalStatusCode {
    const normalizedStatus = String(status ?? '').trim();

    if (normalizedStatus === 'rejected') {
      return 'revision_requested';
    }

    if (
      normalizedStatus === 'imported_pending_fix' ||
      normalizedStatus === 'draft' ||
      normalizedStatus === 'ready_for_teacher' ||
      normalizedStatus === 'teacher_confirmed' ||
      normalizedStatus === 'education_head_confirmed' ||
      normalizedStatus === 'vice_dean_confirmed' ||
      normalizedStatus === 'locked' ||
      normalizedStatus === 'special_revision_requested' ||
      normalizedStatus === 'revision_requested'
    ) {
      return normalizedStatus;
    }

    return 'unknown';
  }

  private normalizeCourseDisplayStatusCode(status: string | null | undefined): CourseDisplayStatusCode {
    if (
      status === 'draft' ||
      status === 'waiting_co_teacher' ||
      status === 'waiting_teacher_submit' ||
      status === 'waiting_education_head_review'
    ) {
      return status;
    }

    return this.normalizeApprovalStatusCode(status);
  }

  private normalizeScheduleStatusCode(status: string | null | undefined): CourseDisplayStatusCode {
    const normalizedStatus = String(status ?? '').trim();

    if (normalizedStatus === 'draft' || normalizedStatus === 'imported_pending_fix') {
      return 'draft';
    }

    if (normalizedStatus === 'rejected') {
      return 'revision_requested';
    }

    if (normalizedStatus === 'submitted') {
      return 'ready_for_teacher';
    }

    if (normalizedStatus === 'approved') {
      return 'education_head_confirmed';
    }

    if (
      normalizedStatus === 'waiting_teacher_submit' ||
      normalizedStatus === 'waiting_education_head_review'
    ) {
      return normalizedStatus;
    }

    return this.normalizeCourseDisplayStatusCode(normalizedStatus);
  }

  private getScheduleListStatusSource(): string | null | undefined {
    const submission = this.scheduleSubmission();
    return submission?.approval_status?.status_code ?? submission?.status;
  }

  private allScheduleDisplayStatuses(): CourseDisplayStatusCode[] {
    return [
      ...(this.scheduleSubmission()?.blocks
        .filter((block) => block.type !== 'project_research_subject' || this.isProjectResearchShownInTimetable(block))
        .map((block) => this.getBlockDisplayStatusCode(block)) ?? []),
      ...this.projectResearchRows().map((row) => row.statusCode),
    ];
  }

  private isPendingScheduleDisplayStatus(status: CourseDisplayStatusCode): boolean {
    return [
      'ready_for_teacher',
      'waiting_co_teacher',
      'waiting_teacher_submit',
    ].includes(status);
  }

  private hasRejectedOrRevisionItem(): boolean {
    return this.scheduleSubmission()?.blocks.some((block) => {
      const rawStatuses = [
        block.status,
        block.approval?.status_code,
        block.approval?.current_status?.code,
      ];

      if (block.type === 'subject_schedule') {
        rawStatuses.push(
          block.assignment_approval?.status_code,
          block.assignment_approval?.current_status?.code,
        );
      }

      return rawStatuses.some((status) => {
        const normalizedStatus = String(status ?? '').trim();
        return normalizedStatus === 'special_revision_requested' ||
          normalizedStatus === 'revision_requested' ||
          normalizedStatus === 'rejected';
      });
    }) ?? false;
  }

  private extractErrorMessage(err: unknown): string {
    return getApiErrorMessage(err);
  }

  private getStatusHistoryLabel(status: StatusHistoryDisplayCode | null | undefined): string {
    const rawStatus = String(status ?? '').trim();
    const labels: Record<StatusHistoryDisplayCode, string> = {
      draft: 'แบบร่าง',
      imported_pending_fix: 'แบบร่าง',
      ready_for_teacher: 'รอดำเนินการ',
      waiting_co_teacher: 'รอดำเนินการ',
      waiting_teacher_submit: 'รอดำเนินการ',
      waiting_education_head_review: 'รอพิจารณารับรอง',
      teacher_confirmed: 'รอพิจารณารับรอง',
      education_head_confirmed: 'รับรองแล้ว',
      vice_dean_confirmed: 'รับรองแล้ว',
      locked: 'รับรองแล้ว',
      special_revision_requested: 'ส่งกลับแก้ไขพิเศษ',
      revision_requested: 'ส่งกลับแก้ไข',
      unknown: '-',
      correction_requested: 'รอดำเนินการ',
      correction_approved: 'รับรองแล้ว',
      correction_rejected: 'ส่งกลับแก้ไข',
      correction_opened: 'รอพิจารณารับรอง',
      correction_completed: 'รับรองแล้ว',
    };

    if (rawStatus in labels) {
      return labels[rawStatus as StatusHistoryDisplayCode];
    }

    const normalizedStatus = this.normalizeStatusHistoryDisplayCode(rawStatus);

    return labels[normalizedStatus];
  }

  private normalizeCorrectionRequestHistoryStatusCode(status: string | null | undefined): CorrectionRequestHistoryStatusCode {
    const normalizedStatus = String(status ?? '').trim();

    if ([
      'correction_requested',
      'correction_approved',
      'correction_rejected',
      'correction_opened',
      'correction_completed',
    ].includes(normalizedStatus)) {
      return normalizedStatus as CorrectionRequestHistoryStatusCode;
    }

    return 'correction_requested';
  }

  private normalizeFlatStatusHistoryCode(status: string | null | undefined): StatusHistoryDisplayCode {
    const normalizedStatus = String(status ?? '').trim();

    if ([
      'correction_requested',
      'correction_approved',
      'correction_rejected',
      'correction_opened',
      'correction_completed',
    ].includes(normalizedStatus)) {
      return this.normalizeCorrectionRequestHistoryStatusCode(normalizedStatus);
    }

    return this.normalizeStatusHistoryDisplayCode(normalizedStatus);
  }

  private normalizeStatusHistoryDisplayCode(status: string | null | undefined): CourseDisplayStatusCode {
    const normalizedStatus = this.normalizeApprovalStatusCode(status);

    if (normalizedStatus === 'imported_pending_fix') {
      return 'imported_pending_fix';
    }

    if (normalizedStatus === 'ready_for_teacher') {
      return 'ready_for_teacher';
    }

    if (normalizedStatus === 'teacher_confirmed') {
      return 'waiting_education_head_review';
    }

    if (['education_head_confirmed', 'vice_dean_confirmed', 'locked'].includes(normalizedStatus)) {
      return 'education_head_confirmed';
    }

    return normalizedStatus;
  }

  private formatDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatHistoryDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const valueByType = new Map(parts.map((part) => [part.type, part.value]));
    const buddhistYear = Number(valueByType.get('year')) + 543;

    return `${valueByType.get('day')}/${valueByType.get('month')}/${buddhistYear} `
      + `${valueByType.get('hour')}:${valueByType.get('minute')}:${valueByType.get('second')}`;
  }

  private normalizeSubjectScheduleDetailResponse(
    response: unknown,
  ): ScheduleSubmissionSubjectScheduleDetailResponse | null {
    if (!this.isRecord(response) || response.modal_type !== 'subject_schedule') {
      return null;
    }

    const block = this.normalizeSubjectScheduleDetailBlock(response.block);

    return block == null
      ? null
      : { ...response, block } as ScheduleSubmissionSubjectScheduleDetailResponse;
  }

  private normalizeSubjectScheduleDetailBlock(value: unknown): SubjectScheduleDetailBlock | null {
    if (!this.isRecord(value) || value.type !== 'subject_schedule') {
      return null;
    }

    const subjectScheduleGroupId = this.toFiniteNumber(value.subject_schedule_group_id);
    const subjectScheduleId = this.toFiniteNumber(value.subject_schedule_id);
    const scheduleSubmissionSubjectScheduleId = this.toFiniteNumber(
      value.schedule_submission_subject_schedule_id,
    );
    const subject = this.normalizeScheduleSubject(value.subject);
    const time = this.normalizeSubjectScheduleTime(value.time);

    if (
      subjectScheduleGroupId == null ||
      subjectScheduleId == null ||
      scheduleSubmissionSubjectScheduleId == null ||
      subject == null ||
      time == null
    ) {
      return null;
    }

    return {
      ...value,
      subject_schedule_group_id: subjectScheduleGroupId,
      subject_schedule_id: subjectScheduleId,
      schedule_submission_subject_schedule_id: scheduleSubmissionSubjectScheduleId,
      subject,
      section: this.normalizeText(value.section) || '-',
      credit: this.toFiniteNumber(value.credit),
      status: this.normalizeText(value.status),
      revision: this.normalizeText(value.revision),
      time,
      teacher_selections: this.normalizeTeacherSelections(value.teacher_selections),
    } as SubjectScheduleDetailBlock;
  }

  private normalizeSubjectScheduleTime(value: unknown): SubjectScheduleDetailBlock['time'] | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const dayOfWeek = this.toFiniteNumber(value.day_of_week);
    const startTime = this.normalizeText(value.start_time);
    const endTime = this.normalizeText(value.end_time);

    if (
      dayOfWeek == null ||
      startTime === '' ||
      endTime === '' ||
      typeof value.is_workload_split !== 'boolean'
    ) {
      return null;
    }

    return {
      ...value,
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
      room_name: value.room_name == null ? null : this.normalizeText(value.room_name),
      is_workload_split: value.is_workload_split,
      teaching_hours: typeof value.teaching_hours === 'number' || typeof value.teaching_hours === 'string'
        ? value.teaching_hours
        : 0,
      teaching_type: this.normalizeSubjectScheduleTeachingType(value.teaching_type),
    } as SubjectScheduleDetailBlock['time'];
  }

  private normalizeSubjectScheduleTeachingType(value: unknown): ScheduleSubmissionTeachingTypeModel {
    if (this.isRecord(value)) {
      return {
        ...value,
        id: this.toFiniteNumber(value.id) ?? 0,
        code: this.normalizeText(value.code),
        name_th: this.normalizeText(value.name_th),
        name_en: this.normalizeText(value.name_en),
      };
    }

    const label = typeof value === 'string' || typeof value === 'number'
      ? this.normalizeText(value)
      : '';

    return {
      id: 0,
      code: '',
      name_th: label,
      name_en: '',
    };
  }

  private normalizeProjectResearchDetailResponse(
    response: unknown,
  ): ScheduleSubmissionProjectResearchDetailResponse | null {
    if (!this.isRecord(response) || response.modal_type !== 'project_research_subject') {
      return null;
    }

    const block = this.normalizeProjectResearchDetailBlock(response.block);

    return block == null
      ? null
      : { ...response, block } as ScheduleSubmissionProjectResearchDetailResponse;
  }

  private normalizeProjectResearchDetailBlock(value: unknown): ProjectResearchSubjectBlockModel | null {
    if (!this.isRecord(value) || value.type !== 'project_research_subject') {
      return null;
    }

    const projectResearchSubjectId = this.toFiniteNumber(value.project_research_subject_id);
    const projectResearchSubjectAssignmentId = this.toFiniteNumber(
      value.project_research_subject_assignment_id,
    );
    const subject = this.normalizeScheduleSubject(value.subject);

    if (projectResearchSubjectId == null || projectResearchSubjectAssignmentId == null || subject == null) {
      return null;
    }

    return {
      ...value,
      project_research_subject_id: projectResearchSubjectId,
      project_research_subject_assignment_id: projectResearchSubjectAssignmentId,
      subject,
      section: this.normalizeText(value.section) || '-',
      status: this.normalizeText(value.status),
      description: value.description == null ? null : this.normalizeText(value.description),
      primary_advisor_student_count: this.toFiniteNumber(value.primary_advisor_student_count) ?? 0,
      co_advisor_student_count: this.toFiniteNumber(value.co_advisor_student_count) ?? 0,
      teaching_times: this.normalizeProjectResearchTeachingTimes(value.teaching_times),
    } as ProjectResearchSubjectBlockModel;
  }

  private normalizeProjectResearchTeachingTimes(value: unknown): ProjectResearchTeachingTimeModel[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is Record<string, unknown> => this.isRecord(item))
      .map((item) => ({
        ...item,
        id: this.toFiniteNumber(item.id) ?? 0,
        day_of_week: this.toFiniteNumber(item.day_of_week) ?? 0,
        start_time: this.normalizeText(item.start_time),
        end_time: this.normalizeText(item.end_time),
        teaching_hours: typeof item.teaching_hours === 'number' || typeof item.teaching_hours === 'string'
          ? item.teaching_hours
          : 0,
      })) as ProjectResearchTeachingTimeModel[];
  }

  private normalizeScheduleSubject(value: unknown): ScheduleSubmissionSubjectModel | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const id = this.toFiniteNumber(value.id);
    const code = this.normalizeText(value.code);
    const name = this.normalizeText(value.name);

    if (id == null || code === '' || name === '') {
      return null;
    }

    return {
      ...value,
      id,
      code,
      name,
      courseunit: this.normalizeText(value.courseunit),
      credit: this.toFiniteNumber(value.credit) ?? 0,
    };
  }

  private normalizeTeacherSelections(value: unknown): ScheduleSubmissionTeacherSelectionModel[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is Record<string, unknown> => this.isRecord(item))
      .map((item) => ({
        ...item,
        user: this.isRecord(item.user) ? item.user : null,
        selected_teaching_dates: this.normalizeSelectedTeachingDates(item.selected_teaching_dates),
      }));
  }

  private normalizeSelectedTeachingDates(value: unknown): ScheduleSubmissionSelectedTeachingDateModel[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is Record<string, unknown> => this.isRecord(item))
      .map((item) => item as ScheduleSubmissionSelectedTeachingDateModel);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
  }

  private toFiniteNumber(value: unknown): number | null {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }

    const numberValue = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private normalizeText(value: unknown): string {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '';
  }

  private loadSubjectScheduleCourseDetail(
    scheduleSubmissionId: number,
    subjectScheduleId: number,
    requestId: number,
  ): void {
    this.scheduleSubmissionsService
      .getSubjectScheduleDetail(scheduleSubmissionId, subjectScheduleId)
      .subscribe({
        next: (response) => {
          if (this.isStaleCourseDetailResponse(requestId)) {
            return;
          }

          try {
            const normalizedResponse = this.normalizeSubjectScheduleDetailResponse(response);

            if (normalizedResponse == null) {
              this.showCourseDetailLoadError(requestId);
              return;
            }

            const subjectScheduleIds = this.getGroupedSubjectScheduleIds(normalizedResponse.block);

            if (subjectScheduleIds.length <= 1) {
              this.selectedCourseDetail.set(this.toSubjectScheduleCourseDetail(normalizedResponse.block));
              this.isCourseDetailLoading.set(false);
              return;
            }

            forkJoin(subjectScheduleIds.map((id) => (
              id === normalizedResponse.block.subject_schedule_id
                ? of(normalizedResponse)
                : this.scheduleSubmissionsService.getSubjectScheduleDetail(scheduleSubmissionId, id)
            ))).subscribe({
              next: (responses) => {
                if (this.isStaleCourseDetailResponse(requestId)) {
                  return;
                }

                try {
                  const blocks = responses
                    .map((item) => this.normalizeSubjectScheduleDetailResponse(item)?.block ?? null);

                  if (blocks.some((block): block is null => block == null)) {
                    this.showCourseDetailLoadError(requestId);
                    return;
                  }

                  this.selectedCourseDetail.set(this.toMergedSubjectScheduleCourseDetail(
                    blocks as SubjectScheduleDetailBlock[],
                  ));
                  this.isCourseDetailLoading.set(false);
                } catch (err) {
                  console.error(err);
                  this.showCourseDetailLoadError(requestId);
                }
              },
              error: (err) => {
                console.error(err);
                this.showCourseDetailLoadError(requestId);
              },
            });
          } catch (err) {
            console.error(err);
            this.showCourseDetailLoadError(requestId);
          }
        },
        error: (err) => {
          console.error(err);
          this.showCourseDetailLoadError(requestId);
        },
      });
  }

  private loadProjectResearchCourseDetail(
    scheduleSubmissionId: number,
    projectResearchSubjectId: number,
    requestId: number,
  ): void {
    if (!Number.isFinite(projectResearchSubjectId)) {
      this.showCourseDetailLoadError(requestId);
      return;
    }

    this.scheduleSubmissionsService
      .getProjectResearchSubjectDetail(scheduleSubmissionId, projectResearchSubjectId)
      .subscribe({
        next: (response) => {
          if (this.isStaleCourseDetailResponse(requestId)) {
            return;
          }

          try {
            const normalizedResponse = this.normalizeProjectResearchDetailResponse(response);

            if (normalizedResponse == null) {
              this.showCourseDetailLoadError(requestId);
              return;
            }

            this.selectedCourseDetail.set(this.toProjectResearchCourseDetail(normalizedResponse.block));
            this.isCourseDetailLoading.set(false);
          } catch (err) {
            console.error(err);
            this.showCourseDetailLoadError(requestId);
          }
        },
        error: (err) => {
          console.error(err);
          this.showCourseDetailLoadError(requestId);
        },
      });
  }

  private loadSnapshotCourseDetail(target: CourseDetailTarget, requestId: number): void {
    if (this.isStaleCourseDetailResponse(requestId)) {
      return;
    }

    try {
      const blocks = this.scheduleSubmission()?.blocks ?? [];

      if (target.type === 'subject_schedule') {
        const subjectBlock = blocks
          .filter((block): block is SubjectScheduleBlockModel => block.type === 'subject_schedule')
          .find((block) => block.subject_schedule_id === target.subjectScheduleId);

        if (subjectBlock == null) {
          this.showCourseDetailLoadError(requestId);
          return;
        }

        const groupedBlocks = blocks
          .filter((block): block is SubjectScheduleBlockModel => block.type === 'subject_schedule')
          .filter((block) => block.subject_schedule_group_id === subjectBlock.subject_schedule_group_id);

        const detail = groupedBlocks.length > 1
          ? this.toMergedSubjectScheduleCourseDetail(groupedBlocks)
          : this.toSubjectScheduleCourseDetail(subjectBlock);

        this.selectedCourseDetail.set(detail);
        this.isCourseDetailLoading.set(false);
        return;
      }

      const projectBlock = blocks
        .filter((block): block is ProjectResearchSubjectBlockModel => block.type === 'project_research_subject')
        .find((block) => block.project_research_subject_id === target.projectResearchSubjectId);

      if (projectBlock == null) {
        this.showCourseDetailLoadError(requestId);
        return;
      }

      this.selectedCourseDetail.set(this.toProjectResearchCourseDetail(projectBlock));
      this.isCourseDetailLoading.set(false);
    } catch (err) {
      console.error(err);
      this.showCourseDetailLoadError(requestId);
    }
  }

  private showCourseDetailLoadError(requestId: number): void {
    if (this.isStaleCourseDetailResponse(requestId)) {
      return;
    }

    this.selectedCourseDetail.set(null);
    this.courseDetailErrorMessage.set('ไม่สามารถโหลดข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง');
    this.isCourseDetailLoading.set(false);
  }

  private isStaleCourseDetailResponse(requestId: number): boolean {
    return requestId !== this.courseDetailRequestId || !this.isCourseDetailModalOpen();
  }

  private loadScheduleSubmission(scheduleSubmissionId: number): void {
    this.scheduleSubmissionId = scheduleSubmissionId;
    this.isLoading.set(true);

    this.scheduleSubmissionsService
      .getScheduleSubmissionTabel(scheduleSubmissionId)
      .subscribe({
        next: (res) => {
          this.liveScheduleSubmission = res;
          this.applyScheduleSubmission(res);
          this.isLoading.set(false);
          if (this.isScheduleVersionViewerEnabled) {
            this.loadVersionOptions(scheduleSubmissionId, res);
          }
        },
        error: (err) => {
          console.error(err);
          this.isLoading.set(false);
        },
      });
  }

  private loadVersionOptions(
    scheduleSubmissionId: number,
    liveSubmission: ScheduleSubmissionsTableModel,
  ): void {
    if (!this.isScheduleVersionViewerEnabled) {
      return;
    }

    this.isVersionListLoading.set(true);
    this.versionErrorMessage.set('');

    this.scheduleSubmissionsService.getScheduleSubmissionVersions(scheduleSubmissionId).subscribe({
      next: (response) => {
        const rawVersions = this.sortVersionOptions(
          this.normalizeCurrentVersionMarkers(response.data, liveSubmission)
        );
        const versions = rawVersions.map((v) => ({
          ...v,
          displayLabel: this.versionSelectLabel(v),
        }));
        this.scheduleVersions.set(versions);
        this.isVersionListLoading.set(false);

        if (versions.length === 0) {
          this.selectedVersionId.set(null);
          this.selectedVersionDetail.set(null);
          return;
        }

        const querySelection = this.versionSelectionFromQueryParam();
        if (querySelection?.kind === 'invalid') {
          this.versionErrorMessage.set('ไม่สามารถแสดงข้อมูล Version นี้ได้');
          this.clearVersionQueryParams();
          this.selectCurrentLiveVersion(versions, liveSubmission);
          return;
        }

        const queryVersion = this.findVersionFromQuerySelection(versions, querySelection);
        const selectedVersion = querySelection != null
          ? queryVersion
          : this.currentVersionForDisplay(versions, liveSubmission);

        if (selectedVersion == null) {
          if (querySelection != null) {
            this.versionErrorMessage.set('ไม่สามารถแสดงข้อมูล Version นี้ได้');
            this.clearVersionQueryParams();
          }
          this.selectCurrentLiveVersion(versions, liveSubmission);
          return;
        }

        this.selectedVersionId.set(selectedVersion.id);
        this.isVersionDropdownOpen.set(false);

        if (querySelection?.kind === 'legacy_id') {
          this.replaceVersionQueryParam(selectedVersion.id);
        }

        if (selectedVersion.is_current) {
          this.selectedVersionDetail.set(null);
          this.applyScheduleSubmission(liveSubmission);
          return;
        }

        this.loadHistoricalVersion(scheduleSubmissionId, selectedVersion.id);
      },
      error: (err) => {
        this.versionErrorMessage.set(this.extractErrorMessage(err));
        this.isVersionListLoading.set(false);
      },
    });
  }

  private selectVersion(versionId: number, updateQueryParam: boolean): void {
    if (!this.isScheduleVersionViewerEnabled) {
      return;
    }

    const version = this.scheduleVersions().find((item) => item.id === versionId);
    const submissionId = this.scheduleSubmissionId;

    if (version == null || submissionId == null) {
      return;
    }

    this.selectedVersionId.set(version.id);
    this.isVersionDropdownOpen.set(false);
    this.versionErrorMessage.set('');

    if (updateQueryParam) {
      this.replaceVersionQueryParam(version.id);
    }

    if (version.is_current) {
      this.selectedVersionDetail.set(null);
      if (this.liveScheduleSubmission != null) {
        this.applyScheduleSubmission(this.liveScheduleSubmission);
      }
      return;
    }

    this.loadHistoricalVersion(submissionId, version.id);
  }

  private selectCurrentLiveVersion(
    versions: ScheduleSubmissionVersionSummary[],
    liveSubmission: ScheduleSubmissionsTableModel,
  ): void {
    const currentVersion = this.currentVersionForDisplay(versions, liveSubmission);
    this.selectedVersionId.set(currentVersion?.id ?? null);
    this.isVersionDropdownOpen.set(false);
    this.selectedVersionDetail.set(null);
    this.applyScheduleSubmission(liveSubmission);
  }

  private versionSelectionFromQueryParam(): VersionQuerySelection {
    const versionId = this.positiveQueryNumber('version_id');
    if (versionId !== null) {
      return versionId === 'invalid' ? { kind: 'invalid' } : { kind: 'id', value: versionId };
    }

    const versionNo = this.positiveQueryNumber('version_no');
    if (versionNo !== null) {
      return versionNo === 'invalid' ? { kind: 'invalid' } : { kind: 'version_no', value: versionNo };
    }

    const legacyVersionId = this.positiveQueryNumber('version');
    if (legacyVersionId !== null) {
      return legacyVersionId === 'invalid'
        ? { kind: 'invalid' }
        : { kind: 'legacy_id', value: legacyVersionId };
    }

    return null;
  }

  private positiveQueryNumber(paramName: string): number | 'invalid' | null {
    const rawValue = this.route.snapshot.queryParamMap.get(paramName);
    if (rawValue == null) {
      return null;
    }

    const trimmedValue = rawValue.trim();
    if (trimmedValue === '') {
      return 'invalid';
    }

    const value = Number(trimmedValue);
    return Number.isFinite(value) && value > 0 ? value : 'invalid';
  }

  private findVersionFromQuerySelection(
    versions: ScheduleSubmissionVersionSummary[],
    selection: VersionQuerySelection,
  ): ScheduleSubmissionVersionSummary | null {
    if (selection == null || selection.kind === 'invalid') {
      return null;
    }

    return versions.find((version) => (
      selection.kind === 'version_no'
        ? version.version_no === selection.value
        : version.id === selection.value
    )) ?? null;
  }

  private currentVersionForDisplay(
    versions: ScheduleSubmissionVersionSummary[],
    liveSubmission: ScheduleSubmissionsTableModel,
  ): ScheduleSubmissionVersionSummary | null {
    const markedCurrentVersion = versions.find((version) => version.is_current);
    if (markedCurrentVersion != null) {
      return markedCurrentVersion;
    }

    if (liveSubmission.current_version_id != null) {
      const currentVersionById = versions.find((version) => version.id === liveSubmission.current_version_id);
      if (currentVersionById != null) {
        return currentVersionById;
      }
    }

    const approvedVersions = versions.filter((version) => version.status === 'approved');
    const latestApprovedVersion = this.latestVersionByVersionNo(approvedVersions);
    if (latestApprovedVersion != null) {
      return latestApprovedVersion;
    }

    return this.latestVersionByVersionNo(versions);
  }

  private normalizeCurrentVersionMarkers(
    versions: ScheduleSubmissionVersionSummary[],
    liveSubmission: ScheduleSubmissionsTableModel,
  ): ScheduleSubmissionVersionSummary[] {
    if (versions.some((version) => version.is_current) || liveSubmission.current_version_id == null) {
      return versions;
    }

    return versions.map((version) => ({
      ...version,
      is_current: version.id === liveSubmission.current_version_id,
    }));
  }

  private sortVersionOptions(
    versions: ScheduleSubmissionVersionSummary[],
  ): ScheduleSubmissionVersionSummary[] {
    return [...versions].sort((current, next) => {
      if (current.is_current !== next.is_current) {
        return current.is_current ? -1 : 1;
      }

      if (current.version_no !== next.version_no) {
        return next.version_no - current.version_no;
      }

      return next.id - current.id;
    });
  }

  private latestVersionByVersionNo(
    versions: ScheduleSubmissionVersionSummary[],
  ): ScheduleSubmissionVersionSummary | null {
    return versions.reduce<ScheduleSubmissionVersionSummary | null>((latestVersion, version) => (
      latestVersion == null || version.version_no > latestVersion.version_no ? version : latestVersion
    ), null);
  }

  private replaceVersionQueryParam(versionId: number): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        version_id: versionId,
        version_no: null,
        version: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearVersionQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        version_id: null,
        version_no: null,
        version: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadHistoricalVersion(
    scheduleSubmissionId: number,
    versionId: number,
  ): void {
    if (!this.isScheduleVersionViewerEnabled) {
      return;
    }

    this.isVersionSnapshotLoading.set(true);
    this.versionErrorMessage.set('');

    this.scheduleSubmissionsService
      .getScheduleSubmissionVersion(scheduleSubmissionId, versionId)
      .subscribe({
        next: (response) => {
          const detail = response.data;

          if (String(detail.snapshot_json.schema_version) !== '1') {
            this.fallbackToLiveVersionDisplay('ไม่สามารถแสดงข้อมูล Version นี้ได้');
            this.isVersionSnapshotLoading.set(false);
            return;
          }

          this.selectedVersionDetail.set(detail);
          const fallbackSubmission = this.liveScheduleSubmission ?? this.scheduleSubmission();
          if (fallbackSubmission == null) {
            this.fallbackToLiveVersionDisplay('ไม่สามารถแสดงข้อมูล Version นี้ได้');
            this.isVersionSnapshotLoading.set(false);
            return;
          }

          this.applyScheduleSubmission(scheduleVersionSnapshotToSubmission(
            detail.snapshot_json,
            fallbackSubmission,
          ));
          this.isVersionSnapshotLoading.set(false);
        },
        error: (err) => {
          this.fallbackToLiveVersionDisplay(this.extractErrorMessage(err));
          this.isVersionSnapshotLoading.set(false);
        },
      });
  }

  private fallbackToLiveVersionDisplay(message: string): void {
    this.versionErrorMessage.set(message);
    this.selectedVersionDetail.set(null);

    if (this.liveScheduleSubmission == null) {
      return;
    }

    const currentVersion = this.currentVersionForDisplay(this.scheduleVersions(), this.liveScheduleSubmission);
    this.selectedVersionId.set(currentVersion?.id ?? null);
    this.isVersionDropdownOpen.set(false);
    this.applyScheduleSubmission(this.liveScheduleSubmission);
  }

  private applyScheduleSubmission(scheduleSubmission: ScheduleSubmissionsTableModel): void {
    this.scheduleSubmission.set(scheduleSubmission);
    this.submissionActionErrorMessage.set('');
    this.teacherName.set(this.formatTeacherName(scheduleSubmission));
    this.departmentName.set(this.getDepartmentName(scheduleSubmission));
    this.timetableRows.set(this.buildTimetableRows(scheduleSubmission.blocks));
    this.projectResearchRows.set(this.buildProjectResearchRows(scheduleSubmission.blocks));
  }

  private getReviewSuccessMessage(response: ScheduleSubmissionsTableModel): string {
    const serviceMessage = String(response.teacher_confirmation?.message ?? '').trim();

    if (serviceMessage !== '') {
      return serviceMessage;
    }

    const action = this.pendingConfirmationAction();

    if (action === 'reject') {
      return 'ส่งอีเมลแจ้งเจ้าหน้าที่เรียบร้อยแล้ว';
    }

    return this.isEducationHeadActionStage()
      ? 'รับรองตารางสอนเรียบร้อยแล้ว'
      : 'ส่งตารางสอนให้หัวหน้างานการศึกษาเรียบร้อยแล้ว';
  }

  private formatTeacherName(scheduleSubmission: ScheduleSubmissionsTableModel): string {
    const fullName = scheduleSubmission.teacher.full_name.trim();
    const academicRank = String(scheduleSubmission.teacher.academic_rank ?? '').trim();

    if (fullName === '') {
      return '-';
    }

    return academicRank === '' ? fullName : `${academicRank} ${fullName}`;
  }

  private getDepartmentName(scheduleSubmission: ScheduleSubmissionsTableModel): string {
    const department = scheduleSubmission.teacher.department;

    return department.name_th ||
      department.name ||
      department.name_en ||
      '-';
  }

  private buildTimetableRows(blocks: ScheduleSubmissionsTableModel['blocks']): TimetableRow[] {
    const eventsByDay = this.toTimetableEvents(blocks).reduce<Record<number, TimetableEvent[]>>(
      (acc, event) => {
        (acc[event.dayOfWeek] ??= []).push(event);
        return acc;
      },
      {}
    );

    return TIMETABLE_DAYS.map((day) => ({
      ...day,
      cells: this.buildTimetableCells(eventsByDay[day.dayOfWeek] ?? []),
    }));
  }

  private toTimetableEvents(blocks: ScheduleSubmissionBlockModel[]): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    return blocks.flatMap((block) => {
      if (block.type === 'subject_schedule') {
        return this.toSubjectScheduleTimetableEvent(block);
      }

      return this.toProjectResearchTimetableEvents(block);
    });
  }

  private toSubjectScheduleTimetableEvent(
    block: SubjectScheduleBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    const event = this.createTimetableEvent({
      dayOfWeek: this.toTimetableDayOfWeek(block.time.day_of_week),
      approvalId: this.getBlockApprovalId(block),
      startTime: block.time.start_time,
      endTime: block.time.end_time,
      subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
      detailLabel: block.time.room_name ?? '',
      statusCode: this.getBlockDisplayStatusCode(block),
      statusLabel: this.getBlockDisplayStatusLabel(block),
      courseDetailTarget: {
        type: 'subject_schedule',
        subjectScheduleId: block.subject_schedule_id,
      },
    });

    return event == null ? [] : [event];
  }

  private toProjectResearchTimetableEvents(
    block: ProjectResearchSubjectBlockModel
  ): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>)[] {
    if (!block.show_in_timetable || block.display_area !== 'timetable') {
      return [];
    }

    return block.teaching_times.flatMap((time) => {
      const event = this.createTimetableEvent({
        dayOfWeek: this.toTimetableDayOfWeek(time.day_of_week),
        approvalId: this.getBlockApprovalId(block),
        startTime: time.start_time,
        endTime: time.end_time,
        subjectLabel: this.formatSubjectLabel(block.subject.code, block.section),
        detailLabel: '',
        statusCode: this.getBlockDisplayStatusCode(block),
        statusLabel: this.getBlockDisplayStatusLabel(block),
        courseDetailTarget: {
          type: 'project_research_subject',
          projectResearchSubjectId: block.project_research_subject_id,
        },
      });

      return event == null ? [] : [event];
    });
  }

  private createTimetableEvent(params: {
    dayOfWeek: number;
    approvalId: number | null;
    startTime: string;
    endTime: string;
    subjectLabel: string;
    detailLabel: string;
    statusCode: CourseDisplayStatusCode;
    statusLabel: string;
    courseDetailTarget: CourseDetailTarget;
  }): (TimetableEvent & Pick<TimetableDay, 'dayOfWeek'>) | null {
    const startSlot = this.toTimetableSlot(params.startTime);
    const endSlot = this.toTimetableSlot(params.endTime);

    if (startSlot == null || endSlot == null || endSlot <= startSlot) {
      return null;
    }

    const clampedStartSlot = Math.max(0, startSlot);
    const clampedEndSlot = Math.min(TIMETABLE_TOTAL_SLOTS, endSlot);

    if (clampedEndSlot <= clampedStartSlot) {
      return null;
    }

    return {
      dayOfWeek: params.dayOfWeek,
      approvalId: params.approvalId,
      startSlot: clampedStartSlot,
      endSlot: clampedEndSlot,
      subjectLabel: params.subjectLabel,
      detailLabel: params.detailLabel,
      statusCode: params.statusCode,
      statusLabel: params.statusLabel,
      courseDetailTarget: params.courseDetailTarget,
    };
  }

  private buildTimetableCells(events: TimetableEvent[]): TimetableCell[] {
    const sortedEvents = events
      .filter((event) => event.endSlot > 0 && event.startSlot < TIMETABLE_TOTAL_SLOTS)
      .sort((a, b) => a.startSlot - b.startSlot || a.endSlot - b.endSlot);
    const cells: TimetableCell[] = [];
    let slot = 0;

    while (slot < TIMETABLE_TOTAL_SLOTS) {
      let nextEvent: TimetableEvent | undefined;

      for (const event of sortedEvents) {
        if (event.startSlot >= slot) {
          nextEvent = event;
          break;
        }
      }

      if (nextEvent == null) {
        this.addEmptyTimetableCells(cells, slot, TIMETABLE_TOTAL_SLOTS);
        break;
      }

      if (nextEvent.startSlot > slot) {
        this.addEmptyTimetableCells(cells, slot, nextEvent.startSlot);
        slot = nextEvent.startSlot;
        continue;
      }

      const group = this.collectOverlappingEvents(sortedEvents, slot);
      const cellStartSlot = slot;

      cells.push({
        startSlot: cellStartSlot,
        colSpan: group.endSlot - cellStartSlot,
        events: group.events.map((event) => ({
          ...event,
          offsetSlots: event.startSlot - cellStartSlot,
          spanSlots: event.endSlot - event.startSlot,
        })),
      });
      slot = group.endSlot;
    }

    return cells;
  }

  private addEmptyTimetableCells(cells: TimetableCell[], startSlot: number, endSlot: number): void {
    let currentSlot = startSlot;

    while (currentSlot < endSlot) {
      const nextHourSlot = Math.ceil((currentSlot + 1) / TIMETABLE_SLOTS_PER_HOUR) * TIMETABLE_SLOTS_PER_HOUR;
      const nextSlot = Math.min(nextHourSlot, endSlot);

      cells.push({
        startSlot: currentSlot,
        colSpan: nextSlot - currentSlot,
        events: [],
      });
      currentSlot = nextSlot;
    }
  }

  private collectOverlappingEvents(
    sortedEvents: TimetableEvent[],
    startSlot: number
  ): { endSlot: number; events: TimetableEvent[] } {
    let endSlot = startSlot;
    const events: TimetableEvent[] = [];
    let hasChange = true;

    while (hasChange) {
      hasChange = false;

      for (const event of sortedEvents) {
        const isOverlapping = event.startSlot < endSlot || (endSlot === startSlot && event.startSlot === startSlot);

        if (!isOverlapping || event.endSlot <= startSlot || events.includes(event)) {
          continue;
        }

        events.push(event);
        endSlot = Math.max(endSlot, event.endSlot);
        hasChange = true;
      }
    }

    return {
      endSlot: Math.min(endSlot, TIMETABLE_TOTAL_SLOTS),
      events,
    };
  }

  private buildProjectResearchRows(blocks: ScheduleSubmissionBlockModel[]): ProjectResearchRow[] {
    return blocks
      .filter((block): block is ProjectResearchSubjectBlockModel => block.type === 'project_research_subject')
      .filter((block) => !this.isProjectResearchShownInTimetable(block))
      .map((block) => ({
        id: block.project_research_subject_assignment_id,
        approvalIds: this.uniqueNumbers([this.getBlockApprovalId(block)]),
        projectResearchSubjectId: block.project_research_subject_id,
        subjectCode: block.subject.code,
        subjectName: block.subject.name,
        teachingDays: this.formatTeachingDays(block.teaching_times),
        teachingTimes: this.formatTeachingTimes(block.teaching_times),
        statusCode: this.getBlockDisplayStatusCode(block),
        statusLabel: this.getBlockDisplayStatusLabel(block),
      }));
  }

  private isProjectResearchShownInTimetable(block: ProjectResearchSubjectBlockModel): boolean {
    return block.show_in_timetable || block.display_area === 'timetable';
  }

  private toSubjectScheduleCourseDetail(block: SubjectScheduleDetailBlock): CourseDetail {
    const teachingHours = this.formatTeachingHours(block.time.workload_cap_hours ?? block.time.teaching_hours);

    return {
      type: 'subject_schedule',
      subjectId: block.subject.id,
      ...this.subjectScheduleApprovalDetail(block),
      approvalStatusCode: this.getBlockDisplayStatusCode(block),
      approvalStatusLabel: this.getBlockDisplayStatusLabel(block),
      courseTypeLabel: 'รายวิชาบรรยาย / ปฏิบัติการ',
      subjectCode: block.subject.code,
      subjectName: block.subject.name,
      curriculumType: this.formatDisplayValue(block.curriculum_type?.name),
      creditDescription: block.subject.courseunit,
      section: block.section,
      studentCount: this.formatDisplayValue(block.student_count),
      teachingDays: this.getDayLabel(block.time.day_of_week),
      teachingTimes: this.formatTimeRange(block.time.start_time, block.time.end_time),
      teachingHours,
      teachingType: this.subjectScheduleTeachingTypeLabel(block),
      workloadSplitLabel: this.workloadSplitLabel(block.time.is_workload_split),
      roomName: this.formatDisplayValue(block.time.room_name),
      primaryAdvisorStudentCount: '-',
      coAdvisorStudentCount: '-',
      description: '-',
      teachers: this.toCourseTeacherDetails(block.teacher_selections ?? [], teachingHours),
    };
  }

  private toMergedSubjectScheduleCourseDetail(blocks: SubjectScheduleDetailBlock[]): CourseDetail {
    const sortedBlocks = this.sortSubjectScheduleBlocks(blocks);
    const primaryBlock = sortedBlocks[0];
    const teachingHours = this.formatTeachingHours(primaryBlock.time.workload_cap_hours ?? primaryBlock.time.teaching_hours);
    const displayStatus = this.getSubjectScheduleGroupDisplayStatus(sortedBlocks);
    const actionStatus = this.getSubjectScheduleGroupActionStatus(sortedBlocks);

    return {
      type: 'subject_schedule',
      subjectId: primaryBlock.subject.id,
      approvalId: this.getBlockApprovalId(primaryBlock),
      approvalIds: this.uniqueNumbers(sortedBlocks.flatMap((block) => [
        block.assignment?.approval?.id,
        block.assignment_approval?.id,
        block.approval?.id,
      ])),
      actionApprovalStatusCode: actionStatus,
      approvalStatusCode: displayStatus,
      approvalStatusLabel: this.getStatusLabel(displayStatus),
      courseTypeLabel: 'รายวิชาบรรยาย / ปฏิบัติการ',
      subjectCode: primaryBlock.subject.code,
      subjectName: primaryBlock.subject.name,
      curriculumType: this.formatDisplayValue(primaryBlock.curriculum_type?.name),
      creditDescription: primaryBlock.subject.courseunit,
      section: primaryBlock.section,
      studentCount: this.formatDisplayValue(primaryBlock.student_count),
      teachingDays: this.formatSubjectScheduleTeachingDays(sortedBlocks),
      teachingTimes: this.formatSubjectScheduleTeachingTimes(sortedBlocks),
      teachingHours,
      teachingType: this.subjectScheduleTeachingTypeLabel(primaryBlock),
      workloadSplitLabel: this.workloadSplitLabel(primaryBlock.time.is_workload_split),
      roomName: this.formatDisplayValue(primaryBlock.time.room_name),
      primaryAdvisorStudentCount: '-',
      coAdvisorStudentCount: '-',
      description: '-',
      teachers: this.toMergedCourseTeacherDetails(sortedBlocks, teachingHours),
    };
  }

  private getGroupedSubjectScheduleIds(block: SubjectScheduleDetailBlock): number[] {
    const groupBlocks = this.scheduleSubmission()?.blocks
      .filter((item): item is SubjectScheduleBlockModel => item.type === 'subject_schedule')
      .filter((item) => item.subject_schedule_group_id === block.subject_schedule_group_id) ?? [];
    const sortedBlocks = this.sortSubjectScheduleBlocks(groupBlocks.length > 0 ? groupBlocks : [block]);

    return this.uniqueNumbers([
      block.subject_schedule_id,
      ...sortedBlocks.map((item) => item.subject_schedule_id),
    ]);
  }

  private sortSubjectScheduleBlocks<T extends Pick<SubjectScheduleBlockModel, 'time'>>(blocks: T[]): T[] {
    return [...blocks].sort((current, next) => {
      const currentDay = this.toTimetableDayOfWeek(current.time.day_of_week);
      const nextDay = this.toTimetableDayOfWeek(next.time.day_of_week);

      if (currentDay !== nextDay) {
        return currentDay - nextDay;
      }

      const currentSlot = this.toTimetableSlot(current.time.start_time) ?? Number.MAX_SAFE_INTEGER;
      const nextSlot = this.toTimetableSlot(next.time.start_time) ?? Number.MAX_SAFE_INTEGER;

      return currentSlot - nextSlot;
    });
  }

  private formatSubjectScheduleTeachingDays(blocks: SubjectScheduleDetailBlock[]): string {
    return this.joinUnique(blocks.map((block) => this.getDayLabel(block.time.day_of_week)));
  }

  private formatSubjectScheduleTeachingTimes(blocks: SubjectScheduleDetailBlock[]): string {
    return this.joinUnique(blocks.map((block) => this.formatTimeRange(block.time.start_time, block.time.end_time)));
  }

  private getSubjectScheduleGroupDisplayStatus(blocks: SubjectScheduleDetailBlock[]): CourseDisplayStatusCode {
    return this.getCourseDisplayStatusByPriority(blocks.map((block) => this.getBlockDisplayStatusCode(block)));
  }

  private getSubjectScheduleGroupActionStatus(blocks: SubjectScheduleDetailBlock[]): ApprovalStatusCode {
    const blockStatuses = blocks.map((block) => this.getBlockActionApprovalStatusCode(block));
    const sharedTeacherStatuses = blocks.flatMap((block) => this.sharedTeacherActionStatuses(block));

    return this.getApprovalStatusByPriority([...blockStatuses, ...sharedTeacherStatuses]);
  }

  private sharedTeacherActionStatuses(block: SubjectScheduleDetailBlock): ApprovalStatusCode[] {
    if (!this.isAdminRole() || !block.time.is_workload_split) {
      return [];
    }

    return (block.teacher_selections ?? [])
      .map((selection) => this.courseTeacherStatusCode(selection));
  }

  private toMergedCourseTeacherDetails(
    blocks: SubjectScheduleDetailBlock[],
    fallbackTeachingHours: string,
  ): CourseTeacherDetail[] {
    const groupedSelections = new Map<string, ScheduleSubmissionTeacherSelectionModel[]>();

    blocks.flatMap((block) => block.teacher_selections ?? []).forEach((selection) => {
      const key = this.teacherSelectionGroupKey(selection);
      groupedSelections.set(key, [...(groupedSelections.get(key) ?? []), selection]);
    });

    return Array.from(groupedSelections.values()).map((selections) => (
      this.toMergedCourseTeacherDetail(selections, fallbackTeachingHours)
    ));
  }

  private toMergedCourseTeacherDetail(
    selections: ScheduleSubmissionTeacherSelectionModel[],
    fallbackTeachingHours: string,
  ): CourseTeacherDetail {
    const primarySelection = selections[0];
    const statusCode = this.getApprovalStatusByPriority(selections.map((selection) => this.courseTeacherStatusCode(selection)));
    const teachingHours = this.sumSelectionTeachingHours(selections);

    return {
      fullName: this.formatDisplayValue(primarySelection.user?.full_name),
      department: this.courseTeacherDepartmentLabel(primarySelection),
      teachingRanges: this.formatSelectedTeachingDateRanges(
        selections.flatMap((selection) => selection.selected_teaching_dates ?? [])
      ),
      teachingHours: teachingHours == null ? fallbackTeachingHours : this.formatTeachingHours(teachingHours),
      statusCode,
      statusLabel: this.courseTeacherStatusLabel(statusCode),
    };
  }

  private teacherSelectionGroupKey(selection: ScheduleSubmissionTeacherSelectionModel): string {
    return String(selection.user_id ?? selection.user?.id ?? selection.user?.full_name ?? '').trim();
  }

  private sumSelectionTeachingHours(selections: ScheduleSubmissionTeacherSelectionModel[]): number | null {
    const values = selections
      .map((selection) => Number(selection.teaching_hours))
      .filter((value) => Number.isFinite(value));

    return values.length === 0
      ? null
      : values.reduce((sum, value) => sum + value, 0);
  }

  private getCourseDisplayStatusByPriority(statuses: CourseDisplayStatusCode[]): CourseDisplayStatusCode {
    const priority: CourseDisplayStatusCode[] = [
      'special_revision_requested',
      'revision_requested',
      'waiting_co_teacher',
      'draft',
      'imported_pending_fix',
      'ready_for_teacher',
      'teacher_confirmed',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
      'unknown',
    ];

    return priority.find((status) => statuses.includes(status)) ?? 'unknown';
  }

  private getApprovalStatusByPriority(statuses: ApprovalStatusCode[]): ApprovalStatusCode {
    const priority: ApprovalStatusCode[] = [
      'special_revision_requested',
      'revision_requested',
      'draft',
      'imported_pending_fix',
      'ready_for_teacher',
      'teacher_confirmed',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
      'unknown',
    ];

    return priority.find((status) => statuses.includes(status)) ?? 'unknown';
  }

  private subjectScheduleApprovalDetail(
    block: SubjectScheduleDetailBlock,
  ): Pick<CourseDetail, 'approvalId' | 'approvalIds' | 'actionApprovalStatusCode'> {
    const assignmentApproval = block.assignment?.approval;
    const displayApproval = block.approval;
    const actionStatus =
      assignmentApproval?.status_code ??
      assignmentApproval?.current_status?.code ??
      block.assignment_approval?.status_code ??
      block.assignment_approval?.current_status?.code ??
      block.status;

    return {
      approvalId: assignmentApproval?.id ?? displayApproval?.id ?? null,
      approvalIds: this.uniqueNumbers([assignmentApproval?.id, displayApproval?.id]),
      actionApprovalStatusCode: this.getApprovalStatusByPriority([
        this.normalizeApprovalStatusCode(actionStatus),
        ...this.sharedTeacherActionStatuses(block),
      ]),
    };
  }

  private subjectScheduleTeachingTypeLabel(
    block: SubjectScheduleDetailBlock,
  ): string {
    const teachingType = block.time?.teaching_type as unknown;

    if (typeof teachingType === 'string' || typeof teachingType === 'number') {
      return this.formatDisplayValue(teachingType);
    }

    if (!this.isRecord(teachingType)) {
      return '-';
    }

    const thaiLabel = this.normalizeText(teachingType.name_th);
    const englishLabel = this.normalizeText(teachingType.name_en);

    return this.formatDisplayValue(
      thaiLabel !== '' ? thaiLabel : englishLabel,
    );
  }

  private workloadSplitLabel(isWorkloadSplit: boolean | null | undefined): string {
    if (isWorkloadSplit === true) {
      return 'แบ่งภาระงานระหว่างผู้สอน';
    }

    if (isWorkloadSplit === false) {
      return 'ไม่แบ่งภาระงานระหว่างผู้สอน';
    }

    return '-';
  }

  private toProjectResearchCourseDetail(
    block: ProjectResearchSubjectBlockModel
  ): CourseDetail {
    return {
      type: 'project_research_subject',
      subjectId: block.subject.id,
      approvalId: block.approval?.id ?? null,
      approvalIds: this.uniqueNumbers([block.approval?.id]),
      actionApprovalStatusCode: this.getBlockActionApprovalStatusCode(block),
      approvalStatusCode: this.getBlockDisplayStatusCode(block),
      approvalStatusLabel: this.getBlockDisplayStatusLabel(block),
      courseTypeLabel: 'รายวิชาโครงงาน / วิจัย',
      subjectCode: block.subject.code,
      subjectName: block.subject.name,
      curriculumType: this.formatDisplayValue(block.curriculum_type?.name),
      creditDescription: block.subject.courseunit,
      section: block.section,
      studentCount: '-',
      teachingDays: this.formatTeachingDays(block.teaching_times),
      teachingTimes: this.formatTeachingTimes(block.teaching_times),
      teachingHours: this.formatTeachingHours(this.sumTeachingHours(block.teaching_times)),
      teachingType: '-',
      workloadSplitLabel: '-',
      roomName: '-',
      primaryAdvisorStudentCount: this.formatNumber(block.primary_advisor_student_count),
      coAdvisorStudentCount: this.formatNumber(block.co_advisor_student_count),
      description: this.formatDisplayValue(block.description),
      teachers: [],
    };
  }

  private toCourseTeacherDetails(
    selections: ScheduleSubmissionTeacherSelectionModel[],
    teachingHours: string,
  ): CourseTeacherDetail[] {
    return selections.map((selection) => this.toCourseTeacherDetail(selection, teachingHours));
  }

  private toCourseTeacherDetail(
    selection: ScheduleSubmissionTeacherSelectionModel,
    teachingHours: string,
  ): CourseTeacherDetail {
    const statusCode = this.courseTeacherStatusCode(selection);

    return {
      fullName: this.formatDisplayValue(selection.user?.full_name),
      department: this.courseTeacherDepartmentLabel(selection),
      teachingRanges: this.formatSelectedTeachingDateRanges(selection.selected_teaching_dates ?? []),
      teachingHours: this.formatTeachingHours(selection.teaching_hours ?? teachingHours),
      statusCode,
      statusLabel: this.courseTeacherStatusLabel(statusCode),
    };
  }

  private courseTeacherDepartmentLabel(selection: ScheduleSubmissionTeacherSelectionModel): string {
    const department = selection.user?.department;

    return this.formatDisplayValue(
      department?.name_th ??
      department?.name ??
      department?.name_en
    );
  }

  private courseTeacherStatusCode(selection: ScheduleSubmissionTeacherSelectionModel): ApprovalStatusCode {
    return this.normalizeApprovalStatusCode(
      selection.approval?.status_code ??
      selection.approval?.current_status?.code ??
      selection.status
    );
  }

  private courseTeacherStatusLabel(statusCode: ApprovalStatusCode): string {
    const labels: Record<ApprovalStatusCode, string> = {
      draft: 'แบบร่าง',
      imported_pending_fix: 'แบบร่าง',
      ready_for_teacher: 'รอดำเนินการ',
      teacher_confirmed: 'รอพิจารณารับรอง',
      education_head_confirmed: 'รับรองแล้ว',
      vice_dean_confirmed: 'รับรองแล้ว',
      locked: 'รับรองแล้ว',
      special_revision_requested: 'ส่งกลับแก้ไขพิเศษ',
      revision_requested: 'ส่งกลับแก้ไข',
      unknown: '-',
    };

    return labels[statusCode];
  }

  private formatSelectedTeachingDateRanges(dates: ScheduleSubmissionSelectedTeachingDateModel[]): string[] {
    const sortedDates = dates
      .map((item) => ({ item, date: this.parseDateOnly(item.date) }))
      .filter((entry): entry is { item: ScheduleSubmissionSelectedTeachingDateModel; date: Date } => entry.date != null)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    const occurrenceByDay = new Map<number, number>();

    return sortedDates.map(({ item, date }) => {
      const dayOfWeek = date.getDay();
      const occurrence = (occurrenceByDay.get(dayOfWeek) ?? 0) + 1;
      const sequenceDay = String(item.sequence_day ?? '')
        .replace(/\s*\([^)]*\)\s*$/, '')
        .trim();

      occurrenceByDay.set(dayOfWeek, occurrence);

      return `${sequenceDay || `${this.getThaiDayLabelFromDate(date)}ที่ ${occurrence}`} - ${this.formatTeachingDate(date)}`;
    });
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));

    if (match == null) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getThaiDayLabelFromDate(date: Date): string {
    return this.getDayLabel(date.getDay());
  }

  private formatTeachingDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;

    return `${day}/${month}/${year}`;
  }

  private formatTeachingDays(teachingTimes: ProjectResearchTeachingTimeModel[]): string {
    const labels = teachingTimes
      .map((time) => this.getDayLabel(time.day_of_week))
      .filter((label, index, items) => label !== '-' && items.indexOf(label) === index);

    return labels.length > 0 ? labels.join(' / ') : '-';
  }

  private formatTeachingTimes(teachingTimes: ProjectResearchTeachingTimeModel[]): string {
    const labels = teachingTimes
      .map((time) => this.formatTimeRange(time.start_time, time.end_time))
      .filter((label) => label !== '-');

    return labels.length > 0 ? labels.join(' / ') : '-';
  }

  private getDayLabel(dayOfWeek: number): string {
    const day = TIMETABLE_DAYS.find((item) => item.dayOfWeek === this.toTimetableDayOfWeek(dayOfWeek));

    return day == null ? '-' : day.thaiName;
  }

  private toTimetableDayOfWeek(dayOfWeek: number): number {
    return dayOfWeek === 0 ? 7 : dayOfWeek;
  }

  private formatTimeRange(startTime: string, endTime: string): string {
    const start = this.formatTime(startTime);
    const end = this.formatTime(endTime);

    if (start === '-' || end === '-') {
      return '-';
    }

    return `${start} - ${end} น.`;
  }

  private toTimetableSlot(value: string): number | null {
    const clockTime = this.parseClockTime(value);

    if (clockTime == null) {
      return null;
    }

    const { hour, minute } = clockTime;
    const minutesFromStart = ((hour - TIMETABLE_START_HOUR) * 60) + minute;

    return Math.floor(minutesFromStart / TIMETABLE_SLOT_MINUTES);
  }

  private parseClockTime(value: string): { hour: number; minute: number } | null {
    const match = /(?:^|T)(\d{1,2}):(\d{2})(?::\d{2})?/.exec(value.trim());

    if (match == null) {
      return null;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return { hour, minute };
  }

  private formatTime(value: string): string {
    const match = /T(\d{2}):(\d{2})/.exec(value);

    if (match != null) {
      return `${Number(match[1])}:${match[2]}`;
    }

    const timeParts = value.split(':');

    if (timeParts.length < 2) {
      return '-';
    }

    return `${Number(timeParts[0])}:${timeParts[1]}`;
  }

  private formatSubjectLabel(code: string, section: string): string {
    return `${code} (${section})`;
  }

  private uniqueNumbers(values: (number | null | undefined)[]): number[] {
    return values.filter((value, index, items): value is number => (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      items.indexOf(value) === index
    ));
  }

  private sumTeachingHours(teachingTimes: ProjectResearchTeachingTimeModel[]): number {
    return teachingTimes.reduce((sum, time) => sum + Number(time.teaching_hours || 0), 0);
  }

  private joinUnique(values: string[]): string {
    const uniqueValues = values
      .map((value) => value.trim())
      .filter((value, index, items) => value !== '' && items.indexOf(value) === index);

    return uniqueValues.length > 0 ? uniqueValues.join(' / ') : '-';
  }

  private formatDisplayValue(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    return String(value);
  }

  formatTeachingHours(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const parsedValue = Number(value);

    if (!Number.isFinite(parsedValue)) {
      return '-';
    }

    return parsedValue.toFixed(2);
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return String(value);
  }

  private navigateBack(): void {
    const destination = this.isApprovalReviewMode()
      ? '/schedule-submissions-management/schedule-submissions-review'
      : '/schedule-submissions-management/schedule-submissions';

    void this.router.navigate([destination]);
  }
}
